/**
 * Social Pulse policy content — structured for a single generic PolicyPage renderer.
 * Each section is either a paragraph (string) or an object with a subtitle
 * and a `bullets` array. Kept out of a database on purpose: policies are
 * versioned in git so legal review is trackable.
 */

export const POLICIES = {
  "terms-of-use": {
    slug: "terms-of-use",
    title: "Terms of Use",
    heading: "Social Pulse — Terms of Use",
    effective: "July 20, 2026",
    updated: "July 23, 2026",
    intro:
      'Welcome to Social Pulse ("Social Pulse," "Social Pulse," "we," "our," or "us"). These Terms of Use ("Terms") govern your access to and use of the Social Pulse website, mobile applications, and related services (collectively, the "Platform"). By accessing or using the Platform, you agree to be bound by these Terms. If you do not agree, you must not use the Platform.',
    sections: [
      {
        n: 1, title: "Our Mission",
        body: "Social Pulse is a global community platform dedicated to celebrating people, communities, charitable organizations, businesses, culture, history, recipes, memorials, positive news, and other information intended to educate, inspire, and connect people around the world.",
      },
      {
        n: 2, title: "Eligibility",
        bullets: [
          "You must be at least 13 years old, or the minimum age required by the laws of your country, to create an account.",
          "If you are under the age of majority, you may only use the Platform with the consent of a parent or legal guardian.",
          "By using the Platform, you represent that you have the legal authority to agree to these Terms.",
        ],
      },
      {
        n: 3, title: "User Accounts",
        body: "You are responsible for:",
        bullets: [
          "Providing accurate information.",
          "Keeping your account information current.",
          "Maintaining the confidentiality of your password.",
          "All activities occurring under your account.",
        ],
        after_bullets: "You agree not to:",
        bullets_2: [
          "Create false identities.",
          "Impersonate another person.",
          "Share your account credentials.",
          "Use automated systems to create accounts.",
        ],
        footer_note: "Social Pulse reserves the right to suspend or terminate accounts that violate these Terms.",
      },
      {
        n: 4, title: "User Content",
        body: "Users may submit:",
        bullets: [
          "Articles", "Memorials", "Recipes", "Comments", "Reviews", "Photographs", "Videos",
          "Event information", "Charity information", "Business listings", "Community information",
          "Historical information", "Links", "Other lawful content",
        ],
        footer_note: "You retain ownership of your content. By submitting content, you grant Social Pulse a worldwide, non-exclusive, royalty-free license to host, display, reproduce, distribute, modify for formatting, translate, archive, and promote your content in connection with operating and improving the Platform. You represent that you have the legal right to publish all submitted content.",
      },
      {
        n: 5, title: "Community Standards",
        body: "Users must treat others with respect. You may not submit content that:",
        bullets: [
          "Is unlawful.", "Is defamatory.", "Is abusive or harassing.",
          "Promotes hatred or discrimination.", "Contains threats or violence.",
          "Is sexually explicit.", "Exploits children.", "Encourages illegal activity.",
          "Contains malware or malicious code.", "Violates intellectual property rights.",
          "Contains spam or deceptive advertising.", "Misrepresents facts or identities.",
        ],
        footer_note: "Social Pulse may remove content that violates these standards.",
      },
      { n: 6, title: "Accuracy of Information", body: "While we strive for accuracy, Social Pulse cannot guarantee that all information is complete, current, or error-free. Information published by users represents their own views and not those of Social Pulse. Users should independently verify important information before relying on it." },
      { n: 7, title: "Memorials and Obituaries", body: "Social Pulse provides tools for preserving memories and celebrating lives. Users submitting memorial information must have appropriate authority or permission where required. Family members or authorized representatives may request corrections or removal of inaccurate information, subject to applicable law and our review process. Social Pulse reserves the right to verify ownership or authority before making significant changes to memorial content." },
      { n: 8, title: "Recipes, Articles, and Contributions", body: "Recipes, articles, photographs, and other submissions should be original or submitted with appropriate permission. Contributors remain responsible for ensuring their content does not infringe copyrights or other legal rights. Social Pulse may edit formatting, categorization, spelling, or metadata without changing the intended meaning." },
      {
        n: 9, title: "Charities and Organizations",
        body: "Information about charities and organizations is provided for informational purposes. Social Pulse does not guarantee:",
        bullets: ["Financial performance.", "Effectiveness.", "Legal status.", "Donation outcomes."],
        footer_note: "Users should conduct their own due diligence before donating or volunteering.",
      },
      { n: 10, title: "External Links", body: "The Platform may include links to third-party websites. Social Pulse does not control or endorse third-party content and is not responsible for their privacy practices, products, services, or information. Accessing third-party websites is at your own risk." },
      { n: 11, title: "Intellectual Property", body: "Unless otherwise stated, the Platform, including its software, design, logos, graphics, text, databases, and trademarks, is owned by or licensed to Social Pulse. You may not copy, reproduce, distribute, modify, reverse engineer, or commercially exploit any part of the Platform without written permission, except where permitted by law." },
      { n: 12, title: "Copyright Complaints", body: "Social Pulse respects intellectual property rights. If you believe your copyrighted work has been used without authorization, you may submit a copyright infringement notice containing sufficient information to identify the work, the allegedly infringing material, your contact details, and a statement made in good faith. We may remove or disable access to disputed content while investigating." },
      { n: 13, title: "Privacy", body: "Your use of the Platform is also governed by the Social Pulse Privacy Policy. Please review the Privacy Policy to understand how personal information is collected, used, and protected." },
      {
        n: 14, title: "Artificial Intelligence",
        body: "Social Pulse may use artificial intelligence to:",
        bullets: [
          "Generate summaries.", "Classify content.", "Improve search.", "Translate information.",
          "Recommend content.", "Detect spam or duplicate submissions.", "Assist moderation.",
        ],
        footer_note: "AI-generated content may contain errors and should not be considered professional advice. Human review may be applied where appropriate.",
      },
      { n: 15, title: "User Ratings and Reviews", body: "Users may submit ratings, reviews, and comments. Reviews should reflect genuine opinions and experiences. Fake reviews, coordinated manipulation, or misleading ratings are prohibited." },
      { n: 16, title: "Social Capital Score", body: "Social Pulse may display reputation or contribution scores based on user participation. These scores are informational only and do not represent legal, financial, or professional qualifications." },
      { n: 17, title: "Donations", body: "Where donation links are provided, donations are generally made directly to the relevant organization unless clearly stated otherwise. Social Pulse is not responsible for how recipient organizations use donated funds." },
      {
        n: 18, title: "Prohibited Uses",
        body: "You agree not to:",
        bullets: [
          "Violate laws.", "Upload malicious software.", "Attempt unauthorized access.",
          "Interfere with Platform security.", "Scrape or harvest data without permission.",
          "Distribute spam.", "Manipulate search results.", "Create fraudulent accounts.",
          "Circumvent security measures.", "Use automated bots except where expressly authorized.",
        ],
      },
      { n: 19, title: "Suspension and Termination", body: "Social Pulse may suspend or terminate accounts that violate these Terms or threaten the security, integrity, or operation of the Platform. Termination does not affect rights or obligations that accrued before termination." },
      { n: 20, title: "Availability", body: "We strive to provide reliable service but do not guarantee uninterrupted or error-free operation. Features may be modified, suspended, or discontinued at any time." },
      { n: 21, title: "Disclaimer", body: 'The Platform is provided "as is" and "as available." To the fullest extent permitted by law, Social Pulse disclaims all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.' },
      { n: 22, title: "Limitation of Liability", body: "To the fullest extent permitted by law, Social Pulse, its officers, employees, volunteers, partners, and affiliates will not be liable for indirect, incidental, consequential, special, exemplary, or punitive damages arising from your use of the Platform. Where liability cannot legally be excluded, it will be limited to the maximum extent permitted by applicable law." },
      { n: 23, title: "Indemnification", body: "You agree to indemnify and hold harmless Social Pulse and its affiliates from claims, damages, losses, liabilities, and expenses arising from your use of the Platform, your submitted content, or your violation of these Terms." },
      { n: 24, title: "Governing Law", body: "These Terms are governed by the laws of the jurisdiction in which Social Pulse a division of Spice N Flavor Inc is incorporated, unless applicable consumer protection laws require otherwise. Any disputes shall be resolved in the competent courts of that jurisdiction, subject to mandatory local legal requirements." },
      { n: 25, title: "Changes to These Terms", body: "Social Pulse may update these Terms from time to time. Updated versions will be posted on the Platform with a revised Effective Date. Continued use of the Platform after changes become effective constitutes acceptance of the revised Terms." },
      {
        n: 26, title: "Contact Us",
        body: "If you have questions about these Terms, please contact:",
        bullets: [
          "Social Pulse(™)",
          "Website: https://www.socialpulse.com",
          "Email: support@socialpulse.com",
          "Portland, Oregon, USA",
        ],
      },
      { n: 27, title: "Entire Agreement", body: "These Terms, together with the Privacy Policy and any additional policies or guidelines referenced herein, constitute the entire agreement between you and Social Pulse regarding your use of the Platform and supersede any prior agreements relating to the same subject matter." },
    ],
    closing: "Thank you for being part of Social Pulse. Together, we can build a respectful, inspiring, and enduring global community that celebrates people, cultures, knowledge, and positive contributions to the world.",
  },

  "privacy-policy": {
    slug: "privacy-policy",
    title: "Privacy Policy",
    heading: "Social Pulse — Privacy Policy",
    effective: "July 20, 2026",
    updated: "July 23, 2026",
    intro:
      'Welcome to Social Pulse ("Social Pulse", "Social Pulse", "we", "our", or "us"). Your privacy is important to us. This Privacy Policy explains how Social Pulse collects, uses, stores, protects, and shares your personal information when you use our website, mobile applications, and related services (collectively, the "Platform"). By using Social Pulse, you agree to the practices described in this Privacy Policy.',
    sections: [
      {
        n: 1, title: "Our Commitment",
        body: "Social Pulse is committed to:",
        bullets: [
          "Respecting your privacy.",
          "Being transparent about how we use your information.",
          "Giving you control over your personal information.",
          "Protecting your data using modern security practices.",
          "Complying with applicable privacy laws around the world.",
        ],
        footer_note: "We do not sell your personal information to advertisers.",
      },
      {
        n: 2, title: "Information We Collect",
        subgroups: [
          { subtitle: "Information You Provide", body: "When you create an account or use the Platform, you may provide:", bullets: ["Name", "Username", "Email address", "Password (encrypted)", "Profile photograph", "Biography", "Country", "State or Province", "City", "Language preference", "Contact information", "Social media links", "Personal interests"], footer_note: "Providing optional profile information is entirely voluntary." },
          { subtitle: "Content You Submit", body: "Depending on the features you use, you may submit:", bullets: ["Articles", "Recipes", "Business listings", "Charity information", "Community information", "Events", "Comments", "Ratings", "Reviews", "Photographs", "Videos", "Documents", "Messages", "Suggestions"], footer_note: "You are responsible for ensuring that you have permission to publish any content you submit." },
          { subtitle: "Automatically Collected Information", body: "When you use the Platform we may collect:", bullets: ["IP address", "Browser type", "Device type", "Operating system", "Device identifiers", "Language settings", "Date and time of access", "Pages viewed", "Search activity", "Clicks and interactions", "Referring website", "Crash reports", "Performance information"] },
          { subtitle: "Location Information", body: "Depending on your device settings and permissions, we may collect:", bullets: ["Country", "State or Province", "City", "Approximate location", "Device location (only with your permission)"], footer_note: "You may disable location permissions at any time through your device settings." },
        ],
      },
      {
        n: 3, title: "Cookies and Similar Technologies",
        body: "Social Pulse uses cookies and similar technologies to:",
        bullets: [
          "Keep you signed in", "Remember your preferences", "Improve security",
          "Measure website performance", "Personalize your experience",
          "Understand how users interact with the Platform",
        ],
        footer_note: "You may manage cookies through your browser settings, although disabling certain cookies may affect functionality.",
      },
      {
        n: 4, title: "How We Use Your Information",
        body: "We use information to:",
        bullets: [
          "Create and manage your account", "Authenticate users", "Provide Platform features",
          "Improve our services", "Personalize content", "Respond to support requests",
          "Send important account notifications", "Detect fraud and abuse",
          "Maintain Platform security", "Improve search functionality",
          "Recommend relevant content", "Comply with legal obligations",
        ],
        footer_note: "We only process personal information for legitimate and lawful purposes.",
      },
      {
        n: 5, title: "Artificial Intelligence",
        body: "Social Pulse uses Artificial Intelligence (AI) to enhance the Platform. AI may assist with:",
        bullets: [
          "Content summaries", "Search improvements", "Content recommendations", "Categorization",
          "Translation", "Spam detection", "Duplicate detection", "Accessibility improvements",
          "Image enhancement", "Data organization",
        ],
        footer_note: "AI-generated content may occasionally contain inaccuracies and should not be considered professional advice. Human moderation may be applied where appropriate.",
      },
      {
        n: 6, title: "Sharing Information",
        body: "We may share information:",
        bullets: [
          "With trusted service providers who assist in operating the Platform",
          "With payment processors where payments are involved",
          "With cloud hosting providers", "To comply with legal obligations",
          "To protect users and the Platform",
          "During a merger, acquisition, or restructuring",
          "With your consent",
        ],
        footer_note: "We do not sell personal information to third parties.",
      },
      {
        n: 7, title: "Public Information",
        body: "Some information you choose to publish may be publicly visible. Examples include:",
        bullets: [
          "User profile", "Articles", "Recipes", "Reviews", "Comments",
          "Charity listings", "Business listings", "Community posts", "Events",
        ],
        footer_note: "Please consider carefully before publishing personal information.",
      },
      { n: 8, title: "International Users", body: "Social Pulse serves users worldwide. Your information may be transferred to and processed in countries other than your own. Where required by law, we implement appropriate safeguards for international data transfers." },
      {
        n: 9, title: "Security",
        body: "We use reasonable administrative, technical, and organizational measures to protect your information, including:",
        bullets: [
          "Encryption during transmission", "Password hashing", "Secure authentication",
          "Access controls", "Firewalls", "Security monitoring",
          "Regular backups", "Software updates",
        ],
        footer_note: "While we work to protect your information, no internet service can guarantee complete security.",
      },
      {
        n: 10, title: "Data Retention",
        body: "We retain personal information only as long as reasonably necessary to:",
        bullets: [
          "Operate the Platform", "Provide requested services", "Meet legal obligations",
          "Resolve disputes", "Enforce our Terms of Use", "Improve our services",
        ],
        footer_note: "Some information may be retained in archived or backup systems for a limited period.",
      },
      {
        n: 11, title: "Your Privacy Rights",
        body: "Depending on where you live, you may have the right to:",
        bullets: [
          "Access your personal information", "Correct inaccurate information",
          "Update your information", "Delete eligible personal information",
          "Restrict certain processing", "Object to certain processing",
          "Receive a copy of your information in a portable format",
          "Withdraw consent where applicable",
        ],
        footer_note: "We will respond to verified requests in accordance with applicable law.",
      },
      {
        n: 12, title: "Account Deletion",
        body: "You may request deletion of your account. Deleting your account may remove your profile and personal information from active systems. Certain information may be retained where necessary for:",
        bullets: ["Legal compliance", "Fraud prevention", "Security", "Financial records", "Historical archives", "Resolving disputes"],
        footer_note: "Where possible, retained information will be minimized or anonymized.",
      },
      { n: 13, title: "Children's Privacy", body: "Social Pulse is not intended for children under the minimum age required by applicable law to create an account without parental consent. We do not knowingly collect personal information from children in violation of applicable law. If we become aware that such information has been collected improperly, we will take reasonable steps to delete it." },
      {
        n: 14, title: "Third-Party Services",
        body: "The Platform may contain links to third-party websites or services, including:",
        bullets: ["Social media platforms", "Video hosting services", "Maps", "Payment providers", "External news sources"],
        footer_note: "These services have their own privacy policies, and Social Pulse is not responsible for their practices.",
      },
      {
        n: 15, title: "Marketing Communications",
        body: "If you subscribe to newsletters or notifications, we may send:",
        bullets: ["Platform updates", "Newsletters", "Feature announcements", "Community news", "Promotional messages"],
        footer_note: "You may unsubscribe from marketing communications at any time using the unsubscribe link or your account settings. Service-related notifications may still be sent when necessary.",
      },
      { n: 16, title: "Changes to This Privacy Policy", body: 'We may update this Privacy Policy from time to time. When significant changes are made, we will post the updated policy on the Platform and revise the "Last Updated" date. Continued use of the Platform after changes become effective constitutes acceptance of the updated Privacy Policy.' },
      {
        n: 17, title: "Contact Us",
        body: "If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact:",
        bullets: [
          "Social Pulse(™)",
          "Website: https://www.socialpulse.com",
          "Email: support@socialpulse.com",
          "Portland, Oregon, USA",
        ],
      },
      { n: 18, title: "Your Trust", body: "Social Pulse exists to inspire, educate, preserve history, celebrate lives, strengthen communities, and connect people around the world. Protecting your privacy is fundamental to that mission. We are committed to continually improving our privacy practices and safeguarding the trust our users place in us." },
    ],
  },

  "memorial-policy": {
    slug: "memorial-policy",
    title: "Memorial & Obituary Policy",
    heading: "Social Pulse Memorial & Obituary Policy — Celebrate a Life",
    effective: "July 20, 2026",
    updated: "July 23, 2026",
    intro: 'Welcome to Social Pulse ("Social Pulse", "Social Pulse", "we", "our", or "us").',
    sections: [
      { n: 1, title: "Our Purpose", body: "The Celebrate a Life section of Social Pulse exists to honour, preserve, and celebrate the lives of people from every country, culture, and community. Our goal is to create a respectful and enduring digital memorial where families, friends, and future generations can remember those who have passed away and preserve their stories for years to come. We believe every life has value, every person has a story, and every generation benefits from remembering those who came before." },
      {
        n: 2, title: "Guiding Principles",
        body: "Social Pulse is committed to:",
        bullets: [
          "Respecting the dignity of every deceased person.", "Treating families with compassion.",
          "Preserving historical information responsibly.", "Encouraging factual and respectful contributions.",
          "Protecting privacy where appropriate.", "Preventing abuse of memorial pages.",
          "Maintaining long-term digital preservation.",
        ],
      },
      {
        n: 3, title: "Who May Create a Memorial",
        body: "A memorial may be created by:",
        bullets: [
          "Immediate family members.", "Extended family members.", "Legal representatives.",
          "Executors or estate representatives.", "Funeral homes.", "Religious organizations.",
          "Community organizations.", "Historical societies.", "Educational institutions.",
          "Employers (where appropriate).", "Verified friends or colleagues.",
          "Other contributors with a legitimate connection or publicly available information.",
        ],
        footer_note: "For historical figures, memorials may be created using reliable public records and reputable historical sources.",
      },
      {
        n: 4, title: "Memorial Ownership",
        body: "The individual who creates a memorial is initially designated as the Memorial Manager. The Memorial Manager may:",
        bullets: [
          "Edit approved content.", "Add photographs.", "Update biographies.",
          "Add family members.", "Add funeral information.",
          "Approve guest contributions (where enabled).", "Manage privacy settings.",
        ],
        footer_note: "Social Pulse reserves the right to transfer management to a verified family member or authorized representative where appropriate.",
      },
      { n: 5, title: "Family Verification", body: "Immediate family members may request verification. Verification may require reasonable evidence of the relationship or authority to act on behalf of the deceased. Verified family members may receive additional management privileges." },
      {
        n: 6, title: "Information That May Be Published",
        body: "A memorial may include:",
        bullets: [
          "Name", "Preferred name", "Photograph", "Biography", "Obituary",
          "Date of birth", "Place of birth", "Date of death", "Place of death",
          "Funeral or memorial service information", "Cemetery or resting place information",
          "Family relationships", "Education", "Career", "Military service",
          "Awards", "Charitable interests", "Photographs", "Videos", "Audio recordings",
          "Guest book messages", "Eulogies", "Historical documents",
          "Newspaper references", "News articles", "Timelines", "Personal stories",
        ],
        footer_note: "Only information that is accurate, lawful, and respectful should be published.",
      },
      {
        n: 7, title: "Information That Should Not Be Published",
        body: "Do not publish:",
        bullets: [
          "Government identification numbers.",
          "Banking or financial information.",
          "Passwords or account credentials.",
          "Medical records that are not appropriate for public disclosure.",
          "Private correspondence without permission.",
          "Information that places surviving family members at risk.",
          "Content that violates applicable privacy laws.",
        ],
      },
      {
        n: 8, title: "Privacy Settings",
        body: "Each memorial may be configured as:",
        bullets: ["Public", "Registered Members Only", "Family Members Only", "Invitation Only", "Private Draft"],
        footer_note: "The Memorial Manager or verified family may change privacy settings where available.",
      },
      {
        n: 9, title: "Obituaries",
        body: "Social Pulse accepts:",
        bullets: [
          "Family-written obituaries.",
          "Funeral home obituaries (where permission has been obtained).",
          "Newspaper obituary references and links.",
          "AI-assisted summaries based on factual information.",
          "Historical obituaries.",
        ],
        footer_note: "Where excerpts are used from third-party publications, contributors must have permission or ensure their use complies with applicable copyright law.",
      },
      {
        n: 10, title: "AI-Assisted Content",
        body: "Social Pulse may use Artificial Intelligence to assist with:",
        bullets: [
          "Biography formatting.", "Timeline generation.", "Grammar and spelling improvements.",
          "Image enhancement.", "Photo restoration.", "Translation.",
          "Duplicate detection.", "Search optimization.", "Suggested categories.",
        ],
        footer_note: "AI-generated content should always be reviewed before publication. AI should not invent facts, relationships, quotations, or events.",
      },
      { n: 11, title: "Family Trees and Relationships", body: "Memorials may include family relationships. These relationships may later be used to build optional family trees. Only relationships supported by reliable information should be added. Users should avoid speculation." },
      {
        n: 12, title: "Guest Book",
        body: "Visitors may leave:",
        bullets: [
          "Condolence messages.", "Personal memories.", "Stories.",
          "Photographs.", "Tributes.", "Virtual candles.", "Virtual flowers.",
        ],
        footer_note: "Guest Book entries must remain respectful. Social Pulse reserves the right to remove inappropriate content.",
      },
      {
        n: 13, title: "Eulogies",
        body: "Family members, friends, colleagues, and community members may submit eulogies. Eulogies should:",
        bullets: [
          "Be respectful.", "Avoid defamatory statements.",
          "Avoid unnecessary speculation.", "Reflect personal experiences where appropriate.",
        ],
      },
      { n: 14, title: "Photographs and Videos", body: "Contributors should upload only content they own or have permission to use. Images should respect the dignity of the deceased and their family. Social Pulse may remove content that is offensive, graphic, or otherwise inappropriate." },
      { n: 15, title: "Historical Memorials", body: "Social Pulse may preserve memorials for historical figures, pioneers, public officials, artists, scientists, athletes, veterans, and other notable individuals. Historical information should be based on reliable sources." },
      { n: 16, title: "Corrections", body: "Anyone may report factual errors. Verified family members receive priority consideration for correction requests relating to personal information. Social Pulse may request supporting evidence before making significant changes." },
      {
        n: 17, title: "Removal Requests",
        body: "Social Pulse understands that families may have concerns about published memorials. Requests may include:",
        bullets: [
          "Correcting inaccuracies.", "Updating photographs.", "Changing privacy settings.",
          "Removing inappropriate content.", "Transferring memorial management.",
        ],
        footer_note: "Each request will be reviewed individually. Social Pulse may decline requests to remove historically significant public information where retention is lawful and serves the public interest.",
      },
      {
        n: 18, title: "Prohibited Content",
        body: "The following is prohibited:",
        bullets: [
          "Harassment.", "Hate speech.", "Personal attacks.", "Defamation.",
          "False information.", "Graphic images intended to shock.",
          "Political campaigning unrelated to the memorial.",
          "Commercial advertising.", "Spam.", "Fraud.", "Impersonation.",
          "Copyright infringement.",
        ],
      },
      { n: 19, title: "Community Standards", body: "Contributors should remember that memorial pages are places of remembrance. Comments should be thoughtful, compassionate, and respectful. Disagreements should never become personal attacks." },
      {
        n: 20, title: "Copyright",
        body: "Contributors remain responsible for ensuring they have permission to publish:",
        bullets: ["Photographs.", "Videos.", "Newspaper excerpts.", "Obituaries.", "Documents.", "Audio recordings."],
        footer_note: "Social Pulse will investigate copyright complaints and may remove content where appropriate.",
      },
      { n: 21, title: "Long-Term Preservation", body: "Social Pulse aims to preserve memorials for future generations. While no online service can guarantee permanent availability, we are committed to maintaining historical records wherever reasonably practicable. As technology evolves, Social Pulse intends to migrate memorials to newer systems to support long-term accessibility." },
      { n: 22, title: "Memorial Archive", body: "Memorials may become part of the Social Pulse Historical Archive. Archived memorials remain searchable unless privacy settings or legal requirements require otherwise. Historical archives help preserve family history and cultural heritage." },
      { n: 23, title: "Changes to This Policy", body: "Social Pulse may update this Memorial & Obituary Policy from time to time. Material changes will be published on the Platform together with the revised effective date." },
      {
        n: 24, title: "Contact Us",
        body: "Questions, correction requests, family verification requests, or memorial management requests may be directed to:",
        bullets: [
          "Social Pulse",
          "Website: https://www.socialpulse.com",
          "Email: support@socialpulse.com",
          "Portland, Oregon, USA",
        ],
      },
      { n: 25, title: "Our Commitment", body: "Every memorial represents a unique human life. Social Pulse is committed to preserving these memories with dignity, compassion, accuracy, and respect. We strive to create a place where families can celebrate loved ones, communities can honour those who made a difference, and future generations can discover the stories of the people who helped shape their families, neighbourhoods, cultures, and the world. Together, we can ensure that every life is remembered and every story has the opportunity to endure." },
    ],
  },

  "cookie-policy": {
    slug: "cookie-policy",
    title: "Cookie Policy",
    heading: "Social Pulse — Cookie Policy",
    effective: "July 23, 2026",
    updated: "July 23, 2026",
    intro:
      'This Cookie Policy explains how Social Pulse ("we," "Social Pulse," "us," or "our") uses cookies and similar technologies when you visit https://www.socialpulse.com (the "Website"). By using our Website, you consent to our use of cookies in accordance with this Cookie Policy, except where applicable law requires us to obtain your consent first.',
    sections: [
      { n: 1, title: "What Are Cookies?", body: "Cookies are small text files that are stored on your device when you visit a website. They help websites function properly, remember your preferences, improve performance, and provide insights into how visitors use the site." },
      {
        n: 2, title: "Types of Cookies We Use",
        subgroups: [
          {
            subtitle: "Essential Cookies",
            body: "These cookies are necessary for the Website to function correctly. They enable core features such as security, navigation, page loading, and access to secure areas. The Website cannot function properly without these cookies.",
          },
          {
            subtitle: "Performance and Analytics Cookies",
            body: "These cookies help us understand how visitors interact with our Website by collecting anonymous or aggregated information, including:",
            bullets: [
              "Pages visited", "Time spent on the Website", "Navigation patterns",
              "Error reports", "Device and browser information",
            ],
            footer_note: "We use this information to improve our Website and user experience.",
          },
          {
            subtitle: "Functional Cookies",
            body: "These cookies remember your preferences and settings, such as language selection or other personalized features, to provide a more convenient experience.",
          },
          {
            subtitle: "Marketing and Advertising Cookies",
            body: "These cookies may be used to:",
            bullets: [
              "Measure the effectiveness of advertising campaigns",
              "Display relevant advertisements",
              "Limit how often you see advertisements",
              "Understand visitor interests",
              "Support remarketing and audience measurement",
            ],
            footer_note: "Some of these cookies may be placed by trusted third-party service providers.",
          },
        ],
      },
      { n: 3, title: "Third-Party Cookies", body: "We may use trusted third-party services that place cookies on our behalf. Depending on the features available on the Website, these services may include analytics providers, advertising partners, embedded content providers, payment processors, customer support tools, and social media integrations. Each third party is responsible for its own privacy and cookie practices." },
      {
        n: 4, title: "Managing Cookies",
        body: "Most web browsers allow you to:",
        bullets: [
          "View stored cookies", "Delete cookies", "Block cookies",
          "Set preferences for specific websites",
          "Receive notifications before cookies are stored",
        ],
        footer_note: "Please note that disabling certain cookies may affect the functionality and performance of the Website.",
      },
      { n: 5, title: "Your Choices", body: "Where required by applicable law, you will be presented with a cookie consent banner that allows you to accept, reject, or customize your cookie preferences. You may also change your preferences at any time through the cookie settings available on the Website, if provided." },
      { n: 6, title: "Changes to This Cookie Policy", body: "We may update this Cookie Policy from time to time to reflect changes in our practices, technology, or legal requirements. The revised version will be posted on this page with an updated Effective Date." },
      {
        n: 7, title: "Contact Us",
        body: "If you have any questions about this Cookie Policy or our use of cookies, please contact:",
        bullets: [
          "Social Pulse(™)",
          "Website: https://www.socialpulse.com",
          "Email: support@socialpulse.com",
          "Portland, Oregon, USA",
        ],
      },
    ],
  },

  "childrens-privacy-policy": {
    slug: "childrens-privacy-policy",
    title: "Children's Privacy Policy",
    heading: "Social Pulse — Children's Privacy Policy",
    effective: "July 20, 2026",
    updated: "July 23, 2026",
    sections: [
      { n: 1, title: "Introduction", body: 'At Social Pulse ("Social Pulse", "Social Pulse", "we", "our", or "us"), protecting the privacy and safety of children is one of our highest priorities. This Children\'s Privacy Policy explains how we collect, use, protect, and manage information relating to children who use the Social Pulse website, mobile applications, and related services (collectively, the "Platform"). This policy should be read together with our Privacy Policy and Terms of Use.' },
      {
        n: 2, title: "Our Commitment",
        body: "Social Pulse is committed to:",
        bullets: [
          "Protecting children's privacy.", "Providing a safe online environment.",
          "Respecting parents' and guardians' rights.",
          "Collecting only information that is reasonably necessary.",
          "Applying privacy-by-design principles.",
          "Complying with applicable child privacy laws around the world.",
        ],
      },
      { n: 3, title: "Who Is a Child?", body: 'For this policy, a "child" means a person who has not reached the minimum age at which they can independently consent to the processing of their personal information under the laws that apply to them. Because this age varies by country, Social Pulse applies local legal requirements where appropriate.' },
      { n: 4, title: "Account Eligibility", body: "Children may only create or use an account where permitted by applicable law. Where parental or guardian consent is required, we may request or verify that consent before certain features become available. If we learn that an account has been created in violation of applicable law, we may suspend or remove the account while the matter is reviewed." },
      {
        n: 5, title: "Information We May Collect",
        body: "When permitted, we may collect information such as:",
        bullets: [
          "First name or display name", "Username", "Email address",
          "Password (stored in encrypted form)", "Country", "Language preference",
          "Profile photograph (optional)", "Content voluntarily submitted",
          "Device information", "Usage information",
          "Technical logs needed for security and service operation",
        ],
        footer_note: "We seek to collect only the minimum information reasonably necessary for the requested service.",
      },
      {
        n: 6, title: "Information We Do Not Intentionally Collect",
        body: "We do not intentionally request or encourage children to provide:",
        bullets: [
          "Government identification numbers", "Financial account information",
          "Credit card information",
          "Biometric identifiers, except where clearly disclosed and legally permitted",
          "Sensitive medical information, unless necessary for a specific feature and handled in accordance with applicable law",
        ],
        footer_note: "If such information is submitted inadvertently, we may remove it.",
      },
      {
        n: 7, title: "How We Use Information",
        body: "We may use children's information to:",
        bullets: [
          "Create and manage accounts", "Provide requested services", "Maintain platform security",
          "Respond to support requests", "Improve accessibility and user experience",
          "Prevent fraud, abuse, and harmful activity", "Comply with legal obligations",
        ],
        footer_note: "We do not use children's personal information for behavioral advertising based on profiling.",
      },
      {
        n: 8, title: "AI Features",
        body: "Social Pulse may use Artificial Intelligence to assist with:",
        bullets: [
          "Content organization", "Translation", "Accessibility", "Search",
          "Spam detection", "Safety monitoring", "Image enhancement", "Educational recommendations",
        ],
        footer_note: "AI systems are designed to support users but should not be relied upon as a substitute for parental guidance or professional advice.",
      },
      {
        n: 9, title: "Parental Rights",
        body: "Where required by applicable law, parents or legal guardians may have the right to:",
        bullets: [
          "Review their child's personal information", "Request corrections",
          "Request deletion of eligible information",
          "Withdraw consent where processing is based on consent",
          "Restrict or object to certain processing",
          "Request closure of a child's account",
        ],
        footer_note: "We may take reasonable steps to verify the identity and authority of the requesting adult before acting on such requests.",
      },
      {
        n: 10, title: "Community Safety",
        body: "Children using Social Pulse must follow our Community Guidelines. The following content is prohibited:",
        bullets: [
          "Bullying or harassment", "Hate speech", "Threats",
          "Grooming or exploitation",
          "Sharing another person's personal information without permission",
          "Violence or encouragement of self-harm", "Illegal content", "Fraud or impersonation",
        ],
        footer_note: "We may suspend or remove accounts or content that violate these standards.",
      },
      { n: 11, title: "Content Involving Children", body: "Users who upload photographs, videos, stories, or other content involving children should have appropriate permission to do so. Content should respect the dignity, privacy, and safety of the child. Social Pulse may remove content that places a child at risk or otherwise violates this policy or applicable law." },
      { n: 12, title: "Memorials Involving Children", body: "The Celebrate a Life section may include memorials for children. Such memorials require particular care and sensitivity. Where possible, memorials relating to minors should be created or managed by a parent, legal guardian, or another authorized representative. We may apply additional privacy protections or moderation to memorials involving children." },
      {
        n: 13, title: "Cookies and Similar Technologies",
        body: "We may use cookies and similar technologies to:",
        bullets: [
          "Maintain login sessions", "Remember preferences", "Improve security",
          "Measure platform performance", "Provide essential functionality",
        ],
        footer_note: "Users and parents may manage cookies through browser or device settings where available.",
      },
      {
        n: 14, title: "Security",
        body: "We use reasonable administrative, technical, and organizational measures designed to protect children's information, including:",
        bullets: [
          "Encryption in transit", "Secure authentication", "Role-based access controls",
          "Security monitoring", "Regular software updates", "Backup and recovery procedures",
        ],
        footer_note: "Although no system can guarantee complete security, we continuously work to improve our safeguards.",
      },
      {
        n: 15, title: "Sharing Information",
        body: "We do not sell children's personal information. We may share information only where necessary:",
        bullets: [
          "With service providers acting on our behalf",
          "To comply with legal obligations",
          "To protect the safety of users",
          "To investigate suspected abuse or illegal activity",
          "During a business restructuring where permitted by law",
        ],
        footer_note: "Any such sharing is subject to appropriate contractual, technical, or legal safeguards.",
      },
      { n: 16, title: "International Users", body: "Social Pulse serves users around the world. Where children's information is transferred internationally, we seek to implement appropriate safeguards in accordance with applicable law." },
      {
        n: 17, title: "Data Retention",
        body: "We retain children's personal information only for as long as reasonably necessary to:",
        bullets: [
          "Provide requested services", "Meet legal obligations",
          "Protect platform security", "Resolve disputes", "Enforce our Terms of Use",
        ],
        footer_note: "When information is no longer needed, we will delete, anonymize, or otherwise handle it in accordance with applicable law.",
      },
      { n: 18, title: "Reporting Concerns", body: "If you believe a child is at risk on Social Pulse, or if content involving a child appears inappropriate, inaccurate, or unsafe, please report it immediately through our reporting tools or contact us directly. We review reports as promptly as practicable and may remove content or restrict accounts where necessary to protect children." },
      { n: 19, title: "Changes to This Policy", body: 'We may update this Children\'s Privacy Policy from time to time. Material changes will be posted on the Platform together with an updated "Last Updated" date. Where required by law, we will provide additional notice before changes take effect.' },
      {
        n: 20, title: "Contact Us",
        body: "If you have questions about this Children's Privacy Policy or wish to exercise parental rights available under applicable law, please contact:",
        bullets: [
          "Social Pulse",
          "Website: https://www.socialpulse.com",
          "Email: support@socialpulse.com",
          "Portland, Oregon, USA",
        ],
      },
      { n: 21, title: "Our Commitment to Children", body: "Social Pulse was created to celebrate people, encourage learning, strengthen communities, preserve memories, and inspire positive action. Children deserve a digital environment that is safe, respectful, and trustworthy. We are committed to continually improving our practices to protect children, respect families, and foster a welcoming community for users of all ages." },
    ],
  },

  // -------------------------------------------------------------------
  // IndianFamilyTree.com — a distinct Terms of Service document. Kept
  // here (rather than in a separate file) so the generic /policies/:slug
  // renderer picks it up unchanged. Surfaced in the footer only on the
  // IndianFamilyTree.com child site — see POLICY_LINKS_IFT below.
  // -------------------------------------------------------------------
  "ift-terms-of-service": {
    slug: "ift-terms-of-service",
    title: "Terms of Service",
    heading: "IndianFamilyTree.com — Terms of Service",
    effective: "August 1, 2026",
    updated: "August 1, 2026",
    intro:
      'Welcome to IndianFamilyTree.com ("IndianFamilyTree," "Platform," "Website," "we," "us," or "our"). IndianFamilyTree.com is a digital family history and genealogy platform created to help individuals and families preserve, discover, and share their family heritage, ancestry, relationships, memories, photographs, documents, stories, and historical records. Our vision is to create a trusted digital archive where Indian families across India and around the world can preserve their roots and pass their legacy to future generations. By accessing or using IndianFamilyTree.com, you agree to these Terms of Service ("Terms"). If you do not agree with these Terms, please do not use our Platform.',
    sections: [
      {
        n: 2, title: "Scope of Services",
        body: "IndianFamilyTree.com provides digital services including:",
        bullets: [
          "Creation and management of family trees",
          "Recording ancestral information",
          "Connecting family members",
          "Preserving family photographs and documents",
          "Creating family history timelines",
          "Recording births, marriages, and deaths",
          "Creating memorial pages and remembrance profiles",
          "Sharing family stories and traditions",
          "Genealogy research assistance",
          "AI-assisted family history tools",
          "Digital storage and family collaboration features",
          "Premium subscription services",
        ],
        after_bullets: "The Platform may introduce additional services in the future.",
      },
      {
        n: 3, title: "Eligibility and User Accounts",
        body: "Users must:",
        bullets: [
          "Provide accurate registration information",
          "Maintain account security",
          "Protect their passwords and login credentials",
          "Use the Platform only for lawful purposes",
        ],
        after_bullets: "Users must generally be at least 18 years old to create an account. Persons below 18 years may use the Platform only with the consent and supervision of a parent or legal guardian.",
      },
      {
        n: 4, title: "Family Tree Information",
        body: "Users may create family records containing information such as:",
        bullets: [
          "Names", "Relationships", "Birth dates", "Marriage details", "Death dates",
          "Places of residence", "Family photographs", "Historical documents",
          "Family stories", "Cultural traditions",
        ],
        after_bullets: "Users are responsible for ensuring that information they submit is truthful, respectful, and legally permitted. IndianFamilyTree.com does not independently verify every family record submitted by users.",
      },
      {
        n: 5, title: "Respect for Privacy of Living Individuals",
        body: "Users should obtain appropriate permission before adding information about living persons. Users should not upload or publish sensitive personal information including:",
        bullets: [
          "Aadhaar numbers", "PAN numbers", "Passport numbers", "Bank details",
          "Medical records", "Private addresses", "Private contact information",
          "Government identity documents",
        ],
        after_bullets: "Users may request correction or removal of their personal information.",
      },
      {
        n: 6, title: "Personal Data Protection",
        body: "IndianFamilyTree.com respects user privacy and processes personal information in accordance with applicable Indian laws, including the Digital Personal Data Protection Act, 2023, the Information Technology Act, 2000, and applicable rules and regulations concerning digital services. Personal information may include name, email address, phone number, family relationships, user-generated content, uploaded photographs and documents, and usage information. We collect and process information only for legitimate purposes including providing genealogy services, maintaining family records, improving platform functionality, communicating with users, and providing requested services. For additional information, please refer to our Privacy Policy.",
      },
      {
        n: 7, title: "Family and Community Information",
        body: "Indian families have diverse traditions, languages, religions, communities, and histories. Users may voluntarily record cultural or historical information such as family traditions, native places, languages, historical occupations, and community heritage. Such information must be provided respectfully and must not promote hate, discrimination, defamation, violence, or social hostility. IndianFamilyTree.com does not endorse or verify personal opinions regarding community identity, ancestry, or historical claims.",
      },
      {
        n: 8, title: "Memorial Pages and Death Records",
        body: "IndianFamilyTree.com allows users to create memorial pages honoring deceased family members and friends. Memorial pages may include name, photograph, biography, family relationships, life achievements, obituary information, funeral details, tributes, condolences, and memories. Users creating memorial pages confirm that they have a legitimate relationship with the deceased person or a reasonable basis for sharing the information. We encourage users to respect family wishes, religious traditions, cultural practices, and privacy concerns. We may remove memorial content that is false, defamatory, illegal, posted without authorization, or harmful to the dignity of the deceased or their family.",
      },
      {
        n: 9, title: "User Content Ownership",
        body: "Users retain ownership of content they upload, including family photographs, stories, documents, genealogy information, and memorial content. By uploading content, users grant IndianFamilyTree.com a limited license to store, display, process, backup, and provide access according to privacy settings. This license exists only to operate and improve the Platform. IndianFamilyTree.com does not claim ownership of family histories or personal memories submitted by users.",
      },
      {
        n: 10, title: "Copyright and Intellectual Property",
        body: "Users must only upload content that they own, or that they have permission to use. Users must not upload copyrighted materials without permission, scanned books or records without rights, unauthorized photographs, or private documents belonging to others. IndianFamilyTree.com respects intellectual property rights and may remove content following valid legal complaints.",
      },
      {
        n: 11, title: "AI-Based Features",
        body: "IndianFamilyTree.com may use artificial intelligence to provide features such as creating family history summaries, organizing family records, generating biographies, translating content, improving photographs, and suggesting connections. AI-generated information may contain errors. Users should review and verify AI-generated content before publishing or relying on it.",
      },
      {
        n: 12, title: "Subscription and Payments",
        body: "Some Platform features may require payment. Paid services may include premium family tree hosting, additional storage, enhanced memorial features, family history reports, and other digital services. Payment terms, pricing, renewal policies, and cancellation options will be displayed before purchase.",
      },
      {
        n: 13, title: "User Responsibilities",
        body: "Users agree not to:",
        bullets: [
          "Create fake family identities",
          "Upload misleading information",
          "Impersonate another person",
          "Violate privacy rights",
          "Upload unlawful content",
          "Harass other users",
          "Attempt unauthorized access",
          "Copy or misuse Platform data",
        ],
      },
      {
        n: 14, title: "Family Tree Accuracy",
        body: "Genealogy records may contain family memories, historical records, user interpretations, and community information. IndianFamilyTree.com does not guarantee that every historical record is accurate. Users should verify important information using official records where required.",
      },
      {
        n: 15, title: "Platform Availability",
        body: "We make reasonable efforts to maintain service availability. However, we do not guarantee continuous access, error-free operation, or permanent storage without interruption. Users are encouraged to maintain personal backups of important family materials.",
      },
      {
        n: 16, title: "Account Suspension and Removal",
        body: "We may restrict or terminate accounts that violate these Terms, harm other users, submit unlawful content, or abuse Platform services. Users may request deletion of their accounts subject to legal and operational requirements.",
      },
      {
        n: 17, title: "Third-Party Services",
        body: "The Platform may use third-party services including payment providers, cloud storage providers, maps, authentication services, and AI services. Third-party services operate under their own terms and privacy policies.",
      },
      {
        n: 18, title: "Limitation of Liability",
        body: "IndianFamilyTree.com provides genealogy and family history services as a digital platform. To the maximum extent permitted under Indian law, we are not responsible for user-submitted inaccuracies, family disputes, historical disagreements, unauthorized submissions by users, or loss caused by user actions.",
      },
      {
        n: 19, title: "Dispute Resolution",
        body: "Any disputes arising from use of IndianFamilyTree.com shall first be addressed through good-faith discussion. If unresolved, disputes shall be subject to applicable Indian laws and the jurisdiction of courts specified by the company.",
      },
      {
        n: 20, title: "Changes to Terms",
        body: "We may update these Terms periodically. Users will be informed of significant changes through appropriate communication channels. Continued use of the Platform indicates acceptance of updated Terms.",
      },
      {
        n: 21, title: "Contact Information",
        body: "IndianFamilyTree.com",
        bullets: [
          "Email: support@indianfamilytree.com",
          "Address: 15166 SE Aston Loop, Portland, OR 97236, USA",
        ],
      },
      {
        n: 22, title: "Our Commitment",
        body: "IndianFamilyTree.com exists to preserve the heritage, memories, and stories of Indian families. Every family has a history. Every generation has a story. Every life leaves a legacy. Our mission is to help families preserve these treasures for generations to come.",
      },
    ],
  },

  // -------------------------------------------------------------------
  // IndianFamilyTree.com — Privacy Policy (aligned with the DPDP Act,
  // 2023, and the Information Technology Act, 2000). Surfaced only on
  // the IndianFamilyTree.com child site via POLICY_LINKS_IFT.
  // -------------------------------------------------------------------
  "ift-privacy-policy": {
    slug: "ift-privacy-policy",
    title: "Privacy Policy",
    heading: "IndianFamilyTree.com — Privacy Policy",
    effective: "August 1, 2026",
    updated: "August 15, 2026",
    intro:
      'Welcome to IndianFamilyTree.com ("IndianFamilyTree," "we," "us," or "our"). IndianFamilyTree.com is a digital genealogy and family heritage platform designed to help individuals and families discover, preserve, organize, and share their family history, relationships, memories, photographs, documents, and ancestral records. We respect your privacy and are committed to protecting your personal data in accordance with applicable laws, including the Digital Personal Data Protection Act, 2023 ("DPDP Act") and other applicable Indian information technology and privacy regulations. This Privacy Policy explains how we collect, process, store, use, share, and protect personal data when you use our website, applications, and related services. By using IndianFamilyTree.com, you acknowledge that you have read and understood this Privacy Policy.',
    sections: [
      {
        n: 2, title: "Who We Are",
        body: "IndianFamilyTree.com operates as a digital family history and genealogy platform. For purposes of applicable data protection laws, IndianFamilyTree.com acts as the Data Fiduciary responsible for determining the purpose and means of processing personal data collected through our services. Contact details of our Data Protection Officer / Grievance Officer:",
        bullets: [
          "Name: Thomas Vellaringattu",
          "Email: support@indianfamilytree.com",
          "Address: 15166 SE Aston Loop, Portland, Oregon 97236, USA",
        ],
      },
      {
        n: 3, title: "Personal Data We Collect",
        body: "We collect only information necessary to provide and improve our services. When you create an account, we may collect your name, email address, mobile number, password or authentication details, profile photograph, and country or location information. Users may voluntarily provide genealogy information including names of family members, relationships, birth information, marriage information, death information, family connections, ancestral locations, family stories, and historical information. Users may upload family photographs, videos, audio recordings, letters, certificates, historical documents, family stories, and memorial content. Users may create memorial records containing the name of deceased persons, date of birth, date of death, biography, photographs, family relationships, life achievements, funeral or remembrance information, tributes and condolences — information about deceased persons may still contain personal data relating to living relatives and must be handled responsibly. For paid services, we may collect subscription details, transaction references, and billing information; payment card details are processed by authorized payment providers and are not stored directly by IndianFamilyTree.com. We may automatically collect IP address, browser information, device information, usage activity, login history, and cookies and similar technologies.",
      },
      {
        n: 4, title: "Purpose of Processing Personal Data",
        body: "We process personal data to provide services (creating family trees, maintaining genealogy records, connecting family members, hosting memorial pages, providing storage services, enabling family collaboration); to improve user experience (personalizing features, improving website performance, developing new services, providing AI-assisted genealogy tools); for communication (sending account notifications, providing service updates, responding to requests, sharing important announcements — marketing communications will be provided according to applicable consent requirements); and for security and fraud prevention (protecting accounts, preventing misuse, detecting unauthorized activity, maintaining platform security).",
      },
      {
        n: 5, title: "Legal Basis for Processing",
        body: "Under the DPDP Act, personal data may be processed based on Consent (where required, we obtain your consent before processing personal data; you may withdraw consent at any time) and Legitimate Uses (processing may occur where permitted by applicable law, including providing requested services, maintaining account security, legal compliance, and protecting rights and safety).",
      },
      {
        n: 6, title: "Consent and User Control",
        body: "By creating an account or submitting information, you authorize IndianFamilyTree.com to process information according to this Privacy Policy. You may:",
        bullets: [
          "Review your information",
          "Update incorrect information",
          "Modify privacy settings",
          "Withdraw consent",
          "Request deletion of your account",
        ],
        after_bullets: "Withdrawal of consent may affect certain services.",
      },
      {
        n: 7, title: "Privacy Settings for Family Trees",
        body: "Users control the visibility of family information. Available options may include:",
        bullets: [
          "Private family tree",
          "Family members only",
          "Invitation-only access",
          "Public historical profile",
          "Public memorial page",
        ],
        after_bullets: "Users should carefully consider privacy settings before sharing information.",
      },
      {
        n: 8, title: "Information About Other People",
        body: "Family tree platforms naturally involve information about multiple individuals. Before adding information about living persons, users should ensure they have appropriate authority or consent where required. Users should not upload:",
        bullets: [
          "Government identification numbers",
          "Financial information",
          "Medical records",
          "Private contact information",
          "Sensitive personal information without authorization",
        ],
      },
      {
        n: 9, title: "Sensitive Personal Information",
        body: "Users should avoid uploading unnecessary sensitive information. This may include:",
        bullets: [
          "Health information",
          "Financial information",
          "Government identity information",
          "Private legal documents",
        ],
        after_bullets: "If users voluntarily submit such information, they are responsible for ensuring appropriate permission exists.",
      },
      {
        n: 10, title: "Children's Personal Data",
        body: "IndianFamilyTree.com may allow families to record information about children as part of family history. Parents or lawful guardians should manage children's profiles and ensure appropriate privacy controls are applied. We do not knowingly collect children's personal data for commercial purposes without appropriate consent.",
      },
      {
        n: 11, title: "AI and Automated Processing",
        body: "IndianFamilyTree.com may use artificial intelligence to provide features such as:",
        bullets: [
          "Family history summaries",
          "Biography generation",
          "Translation",
          "Photo enhancement",
          "Record organization",
          "Relationship suggestions",
        ],
        after_bullets: "AI-generated information may not always be accurate. Users should review AI-generated content before publishing. We do not use AI systems to make legally significant decisions about users.",
      },
      {
        n: 12, title: "Data Sharing",
        body: "We do not sell personal data. We may share information only when necessary with Service Providers such as cloud hosting providers, payment processors, security providers, analytics providers, and technology partners — these providers are required to protect information. We may also disclose information when required by law, court orders, government authorities, or regulatory requirements.",
      },
      {
        n: 13, title: "International Data Transfers",
        body: "IndianFamilyTree.com may serve users globally, including NRIs and international families. Personal data may be processed or stored in locations outside India through trusted technology providers. Such transfers will comply with applicable Indian laws and regulations.",
      },
      {
        n: 14, title: "Data Security",
        body: "We implement reasonable security practices including:",
        bullets: [
          "Encryption where appropriate",
          "Secure authentication",
          "Access controls",
          "Monitoring systems",
          "Backup procedures",
        ],
        after_bullets: "However, no online service can guarantee absolute security.",
      },
      {
        n: 15, title: "Data Retention",
        body: "We retain personal data only as long as necessary for:",
        bullets: [
          "Providing services",
          "Maintaining family records",
          "Meeting legal obligations",
          "Resolving disputes",
          "Protecting legitimate interests",
        ],
        after_bullets: "Users may request deletion subject to applicable legal requirements.",
      },
      {
        n: 16, title: "Your Rights Under DPDP Act",
        body: "Subject to applicable law, users may have rights including:",
        bullets: [
          "Right to Access Information — request details about personal data processed by us",
          "Right to Correction — request correction of inaccurate information",
          "Right to Erasure — request deletion of personal data where applicable",
          "Right to Withdraw Consent — withdraw consent previously provided",
          "Right to Grievance Redressal — submit complaints regarding privacy matters",
        ],
      },
      {
        n: 17, title: "Grievance Redressal",
        body: "If you have concerns regarding privacy or personal data, contact our Grievance Officer:",
        bullets: [
          "Name: Thomas Vellaringattu",
          "Email: support@indianfamilytree.com",
          "Address: 15166 SE Aston Loop, Portland, Oregon 97236, USA",
        ],
        after_bullets: "We will respond within the timelines required under applicable law.",
      },
      {
        n: 18, title: "Cookies",
        body: "We use cookies and similar technologies to maintain user sessions, improve website functionality, understand usage patterns, and enhance user experience. Users may control cookie settings through their browser.",
      },
      {
        n: 19, title: "Third-Party Links",
        body: "Our platform may contain links to external websites. We are not responsible for the privacy practices of third-party websites. Users should review third-party privacy policies separately.",
      },
      {
        n: 20, title: "Changes to This Privacy Policy",
        body: "We may update this Privacy Policy periodically. Material changes will be communicated through appropriate channels. Continued use of the Platform after updates indicates acceptance of the revised policy.",
      },
      {
        n: 21, title: "Contact Us",
        body: "For privacy-related questions:",
        bullets: [
          "IndianFamilyTree.com",
          "Email: support@indianfamilytree.com",
          "Address: 15166 SE Aston Loop, Portland, Oregon 97236, USA",
        ],
      },
      {
        n: 22, title: "Our Privacy Commitment",
        body: "Family history is deeply personal. We recognize that names, photographs, memories, and stories represent generations of family heritage. IndianFamilyTree.com is committed to protecting your trust while helping families preserve their history, honor their loved ones, and pass their legacy to future generations.",
      },
    ],
  },

  // -------------------------------------------------------------------
  // IndianFamilyTree.com — Memorial & Obituary Content Policy.
  // Governs everything from obituary submissions through condolence
  // messages, cultural sensitivity, AI-assist rules, and the removal
  // process. Surfaced only on the IndianFamilyTree.com child site via
  // POLICY_LINKS_IFT.
  // -------------------------------------------------------------------
  "ift-memorial-policy": {
    slug: "ift-memorial-policy",
    title: "Memorial & Obituary Content Policy",
    heading: "IndianFamilyTree.com — Memorial & Obituary Content Policy",
    effective: "August 1, 2026",
    updated: "August 15, 2026",
    intro:
      "At IndianFamilyTree.com, we believe every life has value and every person has a story worth preserving. Our Memorial & Obituary platform exists to help families honor their loved ones, preserve memories, document family history, and create a lasting legacy for future generations. A memorial is more than a record of someone's passing. It is a place to celebrate a person's life journey, their relationships, their achievements, their values, their contributions, and the memories shared by family and friends. We are committed to maintaining a respectful, compassionate, and culturally sensitive environment for remembering those who have passed away.",
    sections: [
      {
        n: 2, title: "Scope of This Policy",
        body: "This policy applies to all memorial-related content submitted to IndianFamilyTree.com, including:",
        bullets: [
          "Obituaries",
          "Memorial pages",
          "Death announcements",
          "Tribute pages",
          "Remembrance posts",
          "Condolence messages",
          "Funeral and memorial service details",
          "Photographs and videos",
          "Family stories",
          "Life histories",
          "Cemetery information",
          "Historical death records",
        ],
      },
      {
        n: 3, title: "Who Can Create a Memorial",
        body: "Memorial pages may be created by:",
        bullets: [
          "Immediate family members",
          "Extended family members",
          "Friends",
          "Community organizations",
          "Religious organizations",
          "Historical researchers",
          "Authorized representatives",
        ],
        after_bullets: "The creator should have a genuine connection to the deceased person or a legitimate reason for preserving the record.",
      },
      {
        n: 4, title: "Accuracy and Authenticity",
        body: "Users should provide accurate information when creating memorial records. Information may include full name, nicknames, maiden names, date of birth, date of death, place of birth, place of death, family relationships, education, career history, achievements, community involvement, and personal stories. IndianFamilyTree.com does not independently verify every memorial submission. Users are encouraged to verify information using reliable sources such as:",
        bullets: [
          "Family records",
          "Government documents",
          "Cemetery records",
          "Religious records",
          "Historical archives",
        ],
      },
      {
        n: 5, title: "Respect for the Deceased and Their Family",
        body: "All memorial content must honor the dignity of the person being remembered. Users should avoid:",
        bullets: [
          "Insults",
          "Defamatory statements",
          "Personal attacks",
          "Family disputes",
          "Hate speech",
          "Discrimination",
          "Mockery",
          "Offensive commentary",
        ],
        after_bullets: "Memorial pages should focus on remembrance, celebration of life, and preserving positive family history.",
      },
      {
        n: 6, title: "Cultural and Religious Sensitivity",
        body: "IndianFamilyTree.com recognizes that families across India and around the world follow diverse traditions. Memorial content may reflect Hindu, Christian, Muslim, Sikh, Buddhist, Jain, tribal, regional, and family practices. Users should respect different approaches to funeral ceremonies, burial and cremation practices, mourning traditions, memorial rituals, and religious beliefs. No memorial should be used to promote religious hatred or discrimination.",
      },
      {
        n: 7, title: "Photographs, Videos and Documents",
        body: "Users may upload family photographs, wedding photographs, childhood memories, achievement photos, videos, letters, certificates, and historical documents. Users confirm that they own the content or have permission to share it. Users should avoid uploading:",
        bullets: [
          "Private documents without permission",
          "Sensitive personal records",
          "Copyrighted materials without authorization",
          "Images that may cause unnecessary distress",
        ],
      },
      {
        n: 8, title: "Obituary Guidelines",
        body: "A respectful obituary may include personal information (full name, date of birth, date of passing, place of residence, family relationships), life story (childhood, education, career, passions, achievements, community service, personal qualities), family information (parents, spouse, children, grandchildren, siblings, extended family), and remembrance information (funeral details, memorial service information, charitable donations, favorite memories).",
      },
      {
        n: 9, title: "Information About Cause of Death",
        body: "Cause of death may be included only when the family chooses to share it, or when it is already publicly available. Users should avoid speculation, rumors, and unverified medical information. Sensitive medical details should not be published without proper authorization.",
      },
      {
        n: 10, title: "Living Persons Mentioned in Memorials",
        body: "Memorial pages often include information about living family members. Users should avoid publishing unnecessary personal information about living individuals, including:",
        bullets: [
          "Phone numbers",
          "Email addresses",
          "Home addresses",
          "Government identification information",
          "Financial information",
        ],
        after_bullets: "Living individuals may request correction or removal of information relating to them.",
      },
      {
        n: 11, title: "Memorial Ownership and Management",
        body: "The person who creates a memorial is considered the initial Memorial Manager. Memorial Managers may:",
        bullets: [
          "Edit information",
          "Add photographs",
          "Approve contributors",
          "Moderate comments",
          "Manage privacy settings",
        ],
        after_bullets: "Families may request transfer of memorial management by providing reasonable evidence of relationship or authorization.",
      },
      {
        n: 12, title: "Family Disputes",
        body: "IndianFamilyTree.com understands that families may sometimes disagree about biographical information, family relationships, memorial ownership, photographs, or historical records. We encourage families to resolve disagreements privately. If disputes arise, we may:",
        bullets: [
          "Temporarily restrict editing",
          "Preserve previous versions",
          "Request supporting documentation",
          "Review complaints",
          "Remove content that violates policies",
        ],
      },
      {
        n: 13, title: "Public and Private Memorial Settings",
        body: "Memorial creators may choose privacy levels such as Public Memorial (visible to anyone), Family Memorial (visible only to approved family members), Private Memorial (visible only to invited users), and Historical Memorial (public records intended for genealogy and historical preservation).",
      },
      {
        n: 14, title: "Condolences and Guestbook Rules",
        body: "Visitors may share memories, condolences, messages of support, and photographs (where permitted). Messages must not include:",
        bullets: [
          "Hate speech",
          "Political arguments",
          "Family conflicts",
          "Advertising",
          "Spam",
          "Offensive content",
        ],
      },
      {
        n: 15, title: "Famous and Historical Persons",
        body: "IndianFamilyTree.com may include memorials for public figures, historical personalities, community leaders, and cultural figures. Such pages should focus on documented historical information and respectful remembrance.",
      },
      {
        n: 16, title: "AI-Assisted Memorial Creation",
        body: "IndianFamilyTree.com may provide AI tools to assist with writing obituaries, organizing life stories, creating timelines, translating memorial content, and generating remembrance materials. AI-generated content should be reviewed by users before publication. AI should not:",
        bullets: [
          "Invent facts",
          "Create false family relationships",
          "Misrepresent historical information",
        ],
      },
      {
        n: 17, title: "Removal of Memorial Content",
        body: "We may remove or restrict memorial content that:",
        bullets: [
          "Violates this policy",
          "Violates applicable laws",
          "Infringes copyrights",
          "Contains false claims",
          "Causes harassment or harm",
          "Was submitted without authorization",
        ],
      },
      {
        n: 18, title: "Reporting Concerns",
        body: "If you believe a memorial violates this policy, please contact our Memorial Support Team at support@indianfamilytree.com. Please include:",
        bullets: [
          "Memorial URL",
          "Your relationship to the deceased",
          "Reason for your concern",
          "Supporting information",
        ],
      },
      {
        n: 19, title: "Digital Legacy and Preservation",
        body: "IndianFamilyTree.com aims to preserve family memories for future generations. However, users should maintain personal copies of important photographs, documents, and family records.",
      },
      {
        n: 20, title: "Memorial Verification Badges",
        body: "To help families quickly recognise the level of trust behind a memorial, IndianFamilyTree.com is introducing a Memorial Verification Badge system with the following levels:",
        bullets: [
          "✅ Family Verified Memorial — confirmed by an immediate family member with proof of relationship",
          "📜 Document Verified Memorial — supported by official records (birth / death / burial / obituary press notice)",
          "🏛 Historical Memorial — belongs to a documented historical or public figure and is maintained editorially",
          "🌳 Connected to Family Tree — the memorial is linked into an active family tree on the platform",
        ],
        after_bullets: "Badges are advisory. They indicate the strength of available verification and do not certify the truth of every biographical detail.",
      },
      {
        n: 21, title: "Our Commitment",
        body: "Every person has a unique journey. Every family carries memories. Every generation inherits stories from those who came before. IndianFamilyTree.com is dedicated to creating a respectful digital space where families can honor their loved ones, preserve their heritage, and ensure that meaningful lives are remembered for generations to come.",
      },
    ],
  },

  // -------------------------------------------------------------------
  // IndianFamilyTree.com — Family Tree Data Ownership & Accuracy Policy.
  // Explains user ownership, contributor responsibilities, handling of
  // living vs deceased persons, conflicting information, DPDP-aligned
  // rights, and dispute handling. Surfaced only on the IFT child site
  // via POLICY_LINKS_IFT below.
  // -------------------------------------------------------------------
  "ift-family-tree-policy": {
    slug: "ift-family-tree-policy",
    title: "Family Tree Data Ownership & Accuracy Policy",
    heading: "IndianFamilyTree.com — Family Tree Data Ownership & Accuracy Policy",
    effective: "August 1, 2026",
    updated: "August 15, 2026",
    intro:
      "At IndianFamilyTree.com, we believe family history is a shared heritage that connects generations. A family tree is not merely a collection of names and dates. It represents relationships, memories, traditions, photographs, documents, and stories passed from one generation to another. Our goal is to provide a trusted platform where Indian families and communities can preserve their heritage while respecting privacy, accuracy, and the rights of individuals. This policy explains who owns family tree information, how information may be added and managed, responsibilities of contributors, how accuracy is maintained, how disputes are handled, and how family history is preserved responsibly.",
    sections: [
      {
        n: 2, title: "Nature of Family Tree Information",
        body: "Family tree information may include:",
        bullets: [
          "Names",
          "Family relationships",
          "Birth information",
          "Marriage information",
          "Death information",
          "Ancestral villages and places",
          "Family photographs",
          "Historical documents",
          "Family stories",
          "Cultural traditions",
          "Occupations",
          "Education history",
          "Achievements",
          "Migration history",
        ],
        after_bullets: "Such information may relate to living persons, deceased persons, or historical ancestors.",
      },
      {
        n: 3, title: "Ownership of Family Tree Data",
        body: "Users retain ownership of original content they upload, including personal photographs, family documents, stories, written biographies, and personal memories. IndianFamilyTree.com does not claim ownership of user-submitted family materials. Family relationship information, however, represents shared heritage and may involve multiple family members. Examples include a person's relationship as a parent, child, sibling, or spouse, an ancestor's name and historical details, and family lineage information. Such information may not belong exclusively to one individual because it represents connections between multiple people and generations. IndianFamilyTree.com provides tools for families to collaboratively preserve and manage such information.",
      },
      {
        n: 4, title: "User Rights and Responsibilities",
        body: "Users adding information to a family tree agree that they will provide information honestly and accurately, respect the privacy of living persons, avoid knowingly adding false information, avoid harmful or defamatory statements, respect family traditions and relationships, and correct errors when identified. Users should not use the platform to:",
        bullets: [
          "Alter family history maliciously",
          "Remove legitimate family members",
          "Create false relationships",
          "Discredit relatives",
          "Publish private information without permission",
        ],
      },
      {
        n: 5, title: "Family Tree Contributors",
        body: "Family trees may have multiple contributors, including:",
        bullets: [
          "Family administrators",
          "Parents",
          "Children",
          "Relatives",
          "Genealogy researchers",
          "Historical contributors",
        ],
        after_bullets: "Contributors may have different permissions based on access settings.",
      },
      {
        n: 6, title: "Family Tree Administrator Role",
        body: "The Family Tree Creator or Administrator may manage invitations, contributor access, privacy settings, approvals, merging of duplicate profiles, and corrections. Administrators should act responsibly and fairly. Administrators should not delete legitimate family members without reason, block rightful relatives from participating, or change historical information intentionally.",
      },
      {
        n: 7, title: "Information About Living Persons",
        body: "Special care must be taken when adding living individuals. Users should avoid adding unnecessary personal information including:",
        bullets: [
          "Aadhaar numbers",
          "PAN information",
          "Passport details",
          "Financial information",
          "Medical records",
          "Private addresses",
          "Personal contact details",
        ],
        after_bullets: "Living individuals may request correction of information, restriction of visibility, or removal of their personal information. Requests will be handled according to applicable Indian privacy laws, including the Digital Personal Data Protection Act, 2023.",
      },
      {
        n: 8, title: "Information About Deceased Persons",
        body: "IndianFamilyTree.com recognizes that deceased family members are an important part of family heritage. Information about deceased persons may include names, dates, family relationships, photographs, life stories, and historical information. Families should respect the dignity of the deceased, religious traditions, cultural practices, and family wishes.",
      },
      {
        n: 9, title: "Genealogy Accuracy Standards",
        body: "IndianFamilyTree.com encourages users to maintain accurate records. Recommended sources include:",
        bullets: [
          "Birth certificates",
          "Marriage certificates",
          "Death certificates",
          "Church records",
          "Temple records",
          "Mosque records",
          "Government records",
          "School records",
          "Land records",
          "Family documents",
          "Historical archives",
        ],
        after_bullets: "Users should clearly distinguish between Verified Information (supported by reliable records), Family Tradition (information passed through generations but not officially documented), and Research Notes (information under investigation).",
      },
      {
        n: 10, title: "Handling Conflicting Information",
        body: "Family histories may contain differences regarding dates, names, spelling, places, relationships, and historical events. When conflicts occur, IndianFamilyTree.com encourages users to discuss respectfully, provide supporting evidence, and preserve multiple viewpoints when appropriate. The platform may allow alternative names, historical notes, multiple sources, and revision history.",
      },
      {
        n: 11, title: "Name Variations and Indian Naming Traditions",
        body: "Indian names may change due to regional languages, transliteration differences, marriage customs, caste/community naming traditions, initial-based names, village names, and family names. IndianFamilyTree.com supports:",
        bullets: [
          "Multiple spellings",
          "Native language names",
          "Maiden names",
          "Previous names",
          "Nicknames",
          "Alternate names",
        ],
        after_bullets: "Users should avoid changing names in a way that misrepresents identity or heritage.",
      },
      {
        n: 12, title: "Caste, Community and Ancestry Information",
        body: "Some Indian families may record historical information relating to communities, villages, lineages, clans, and family traditions. Such information must be used for historical preservation, genealogy research, and family heritage. It must not be used for discrimination, hate speech, harassment, or social exclusion.",
      },
      {
        n: 13, title: "DNA and Genetic Information",
        body: "IndianFamilyTree.com does not require DNA information. Users should exercise caution before sharing genetic information. Users should understand that DNA information may reveal information about family relationships, biological connections, and ancestry. DNA-related features, if introduced, will be governed by additional policies and consent requirements.",
      },
      {
        n: 14, title: "Duplicate Profiles and Merging Records",
        body: "Duplicate records may occur because of different spellings, regional languages, multiple contributors, and historical records. IndianFamilyTree.com may provide tools to identify duplicates, suggest merges, and preserve historical versions. Important relationships and information should not be removed during merging.",
      },
      {
        n: 15, title: "Family Disputes and Claims",
        body: "IndianFamilyTree.com is not responsible for resolving inheritance disputes, property disputes, family disagreements, or legal claims regarding ancestry. Family trees are historical and genealogical records, not legal proof of ownership, succession rights, citizenship, or legal inheritance. Users should consult appropriate legal authorities for legal matters.",
      },
      {
        n: 16, title: "Data Corrections and Appeals",
        body: "Users may request corrections by providing:",
        bullets: [
          "The affected profile",
          "The requested correction",
          "Supporting evidence",
          "Relationship to the person",
        ],
        after_bullets: "IndianFamilyTree.com may review evidence provided, privacy concerns, community impact, and historical records.",
      },
      {
        n: 17, title: "Data Export and Family Legacy",
        body: "Users may request access to their family information where available. We encourage families to preserve copies of important photographs, documents, stories, and historical records. Family history is a valuable legacy that should not depend on a single platform.",
      },
      {
        n: 18, title: "AI and Family History Assistance",
        body: "IndianFamilyTree.com may use AI tools to organize records, suggest relationships, generate timelines, and summarize family stories. AI suggestions are not guaranteed to be correct. Users remain responsible for reviewing and approving information before publication.",
      },
      {
        n: 19, title: "Platform Integrity",
        body: "IndianFamilyTree.com reserves the right to remove fraudulent records, restrict abusive users, preserve historical versions, protect family privacy, and maintain the integrity of genealogical information.",
      },
      {
        n: 20, title: "Our Commitment",
        body: "Family trees connect the past, present, and future. Our mission is to help Indian families worldwide preserve their roots, honor their ancestors, strengthen family bonds, and ensure that future generations can discover the stories of those who came before them. Every family has a history. Every ancestor has a story. Every generation has a legacy worth preserving.",
      },
      {
        n: 21, title: "Contact Information",
        body: "For questions regarding family tree data:",
        bullets: [
          "IndianFamilyTree.com",
          "Email: support@indianfamilytree.com",
          "Address: 15166 SE Aston Loop, Portland, Oregon 97236, USA",
        ],
      },
    ],
  },

  // -------------------------------------------------------------------
  // IndianFamilyTree.com — User Consent & Authorization Policy.
  // DPDP-Act aligned. Explains how consent is obtained, categories of
  // consent, consent for minors, consent withdrawal, correction rights,
  // and dispute handling. Surfaced only on the IFT child site via
  // POLICY_LINKS_IFT.
  // -------------------------------------------------------------------
  "ift-consent-policy": {
    slug: "ift-consent-policy",
    title: "User Consent & Authorization Policy",
    heading: "IndianFamilyTree.com — User Consent & Authorization Policy",
    effective: "August 1, 2026",
    updated: "August 15, 2026",
    intro:
      "At IndianFamilyTree.com, we recognize that family history is a shared heritage. Creating a family tree often involves information about multiple generations, including parents, grandparents, ancestors, children, relatives, and community members. This policy explains how we obtain, manage, and document user consent and authorization when individuals create, contribute to, or manage family records on our platform. Our goal is to balance the preservation of family heritage, respect for individual privacy, compliance with Indian data protection laws, and responsible sharing of personal information.",
    sections: [
      {
        n: 2, title: "Legal Framework",
        body: "IndianFamilyTree.com follows applicable Indian laws and regulations relating to digital information protection, including:",
        bullets: [
          "Digital Personal Data Protection Act, 2023 (DPDP Act)",
          "Information Technology Act, 2000",
          "Applicable rules and regulations governing digital services",
        ],
        after_bullets: "Under the DPDP Act, IndianFamilyTree.com acts as a Data Fiduciary responsible for processing personal data in accordance with applicable requirements.",
      },
      {
        n: 3, title: "Acceptance of Consent",
        body: "By registering an account, using IndianFamilyTree.com, or submitting family information, users acknowledge that they have read and accepted the Terms of Service, Privacy Policy, Family Tree Data Ownership & Accuracy Policy, and this User Consent & Authorization Policy. Consent may be obtained through:",
        bullets: [
          "Account registration",
          "Checkbox acceptance",
          "Digital confirmation",
          "Email verification",
          "Mobile OTP verification",
          "Other legally acceptable electronic methods",
        ],
      },
      {
        n: 4, title: "Types of Consent",
        body: "IndianFamilyTree.com may obtain different types of consent depending on the service being used. A. Account Consent — when creating an account, users consent to account creation, identity verification, service communication, and platform operation. B. Family Tree Contribution Consent — users may add names, relationships, dates, locations, and historical details, confirming they have a legitimate family or historical connection or a reasonable basis for contributing the information. C. Consent for Living Persons — before adding detailed information about a living person, users should obtain appropriate permission where required. Users should respect the wishes of individuals who do not want their information displayed.",
      },
      {
        n: 5, title: "Authorization to Create Family Trees",
        body: "The person who creates a family tree becomes the initial Family Tree Creator / Administrator. The administrator may invite relatives, manage access, approve contributors, edit information, and manage privacy settings. The administrator does not become the owner of other family members' personal information. Family history represents shared relationships among multiple people.",
      },
      {
        n: 6, title: "Authorization to Add Another Person's Information",
        body: "Before adding another person's information, users confirm that they have a family relationship, permission, or a legitimate genealogy purpose. Users should not add information for:",
        bullets: [
          "Harassment",
          "Personal disputes",
          "Commercial misuse",
          "False representation",
        ],
      },
      {
        n: 7, title: "Consent for Photographs and Media",
        body: "Users uploading family photographs, videos, audio recordings, documents, or letters confirm that they own the material or have permission to share it. Users should consider consent before uploading photographs containing living relatives, children, private family events, or sensitive situations.",
      },
      {
        n: 8, title: "Consent for Memorial Pages and Obituaries",
        body: "IndianFamilyTree.com allows users to create memorials for deceased persons. The memorial creator confirms that they have a relationship with the deceased or a legitimate reason for creating the memorial. Memorial creators may add obituaries, photographs, life stories, family relationships, and funeral information. However, they should respect family wishes, religious customs, and the privacy concerns of surviving relatives.",
      },
      {
        n: 9, title: "Consent for Minors' Information",
        body: "Children's information requires special care. Parents or lawful guardians may create or manage profiles for minors. Users should avoid publishing unnecessary information about children, including:",
        bullets: [
          "School details",
          "Location information",
          "Contact details",
          "Personal schedules",
        ],
        after_bullets: "IndianFamilyTree.com may apply additional privacy protections for minors.",
      },
      {
        n: 10, title: "Consent Withdrawal",
        body: "Users may withdraw consent where applicable. Withdrawal requests may include removing personal information, restricting visibility, deleting account information, and removing uploaded content. Withdrawal of consent may affect certain services that depend on the information.",
      },
      {
        n: 11, title: "Correction and Update Requests",
        body: "Individuals may request correction of inaccurate information. Requests may include:",
        bullets: [
          "Incorrect names",
          "Wrong relationships",
          "Incorrect dates",
          "Incorrect photographs",
          "Incorrect personal details",
        ],
        after_bullets: "IndianFamilyTree.com may request reasonable verification before making changes.",
      },
      {
        n: 12, title: "Consent for Public Family Records",
        body: "Some users may choose to make information publicly available for historical research, genealogy discovery, community heritage, and family connections. Before making information public, users should understand that publicly available information may be viewed, shared, or indexed by search engines.",
      },
      {
        n: 13, title: "Privacy Settings and Access Control",
        body: "Users may choose visibility options such as Private (only the account owner can view), Family Only (approved relatives can access), Invitation Only (specific individuals can access), and Public (available to broader audiences). Users are responsible for selecting appropriate privacy settings.",
      },
      {
        n: 14, title: "Consent for AI Features",
        body: "IndianFamilyTree.com may use AI tools for organizing family information, creating family summaries, generating timelines, translating family stories, and suggesting possible relationships. Users authorize processing necessary for these features. AI-generated information should always be reviewed before publication.",
      },
      {
        n: 15, title: "Third-Party Contributors",
        body: "Organizations such as genealogy researchers, historical societies, religious institutions, and community organizations may contribute records only when they have appropriate authorization. They must comply with copyright requirements, privacy obligations, and applicable laws.",
      },
      {
        n: 16, title: "Family Disputes Regarding Consent",
        body: "Family members may disagree about whether information should be published, who can edit a family tree, ownership of photographs, or accuracy of relationships. IndianFamilyTree.com may:",
        bullets: [
          "Temporarily restrict changes",
          "Review complaints",
          "Preserve previous versions",
          "Request supporting documentation",
        ],
        after_bullets: "We encourage families to resolve personal disputes respectfully.",
      },
      {
        n: 17, title: "Evidence of Consent",
        body: "IndianFamilyTree.com may maintain records of registration consent, privacy selections, content submissions, access permissions, and account activity. Such records help demonstrate compliance and protect users.",
      },
      {
        n: 18, title: "Data Protection and Security",
        body: "Consent information is protected using reasonable security practices including access controls, authentication systems, secure storage, and monitoring procedures.",
      },
      {
        n: 19, title: "User Responsibility",
        body: "Users are responsible for respecting relatives' privacy, obtaining permissions where appropriate, providing accurate information, and using the platform responsibly. Users should not assume that family relationships automatically provide unlimited permission to publish personal information.",
      },
      {
        n: 20, title: "Grievance and Support",
        body: "For concerns regarding consent, authorization, or privacy, please reach out to us at:",
        bullets: [
          "IndianFamilyTree.com Privacy Support",
          "Email: support@indianfamilytree.com",
          "Grievance Officer: Thomas Vellaringattu",
          "Address: 15166 SE Aston Loop, Portland, Oregon 97236, USA",
        ],
      },
      {
        n: 21, title: "Our Commitment",
        body: "Family history belongs to generations. Preserving it requires trust, respect, and responsibility. IndianFamilyTree.com is committed to helping families document their heritage while protecting the dignity, privacy, and choices of every individual whose story becomes part of our shared human history. Preserve the past. Respect the present. Inspire future generations.",
      },
    ],
  },

  // -------------------------------------------------------------------
  // IndianFamilyTree.com — Data Deletion & Legacy Account Policy.
  // Explains how users can delete their data, what happens to shared
  // family history when an account is closed, and how Legacy Managers
  // preserve records after a user is no longer active. Surfaced only
  // on the IFT child site via POLICY_LINKS_IFT.
  // -------------------------------------------------------------------
  "ift-deletion-legacy-policy": {
    slug: "ift-deletion-legacy-policy",
    title: "Data Deletion & Legacy Account Policy",
    heading: "IndianFamilyTree.com — Data Deletion & Legacy Account Policy",
    effective: "August 1, 2026",
    updated: "August 15, 2026",
    intro:
      "At IndianFamilyTree.com, we recognize that family history is a legacy passed from one generation to another. Unlike ordinary digital accounts, genealogy accounts may contain valuable historical information including family relationships, ancestral records, photographs, documents, family stories, memorial pages, and historical information. This policy explains how users can request deletion of their information and how IndianFamilyTree.com manages accounts when users are no longer able to manage them. Our goal is to balance individual privacy rights, family heritage preservation, legal obligations, and respect for deceased persons and their families.",
    sections: [
      {
        n: 2, title: "Scope of This Policy",
        body: "This policy applies to:",
        bullets: [
          "Registered user accounts",
          "Family tree records",
          "Memorial pages",
          "Uploaded photographs",
          "Documents",
          "Stories",
          "Comments",
          "Contributions",
          "Legacy accounts",
        ],
      },
      {
        n: 3, title: "User Rights Regarding Their Personal Data",
        body: "Subject to applicable laws, including the Digital Personal Data Protection Act, 2023 (DPDP Act), users may request:",
        bullets: [
          "Access to their personal data",
          "Correction of inaccurate information",
          "Withdrawal of consent",
          "Deletion of personal data",
          "Closure of their account",
        ],
        after_bullets: "Requests will be processed after reasonable verification of identity.",
      },
      {
        n: 4, title: "Account Deletion Request",
        body: "Users may request deletion of their account through account settings, a written request, or an email request to our support team. Deletion requests should include:",
        bullets: [
          "Registered email address",
          "User name",
          "Account details",
          "Verification information",
        ],
      },
      {
        n: 5, title: "What Happens When an Account Is Deleted",
        body: "When a verified deletion request is approved, Personal Account Information (name, email address, phone number, login information, profile information) may be deleted or anonymized. Users may request removal of their User-Generated Content — photos, stories, documents, personal uploads, and comments. Because genealogy information represents relationships among multiple individuals, deletion of Family Tree Information requires careful handling: the user's account may be removed and their personal profile information deleted or anonymized, but family relationships already incorporated into a shared tree may be preserved where appropriate. Other family members' information will not automatically be deleted.",
      },
      {
        n: 6, title: "Family Tree Preservation After Account Deletion",
        body: "IndianFamilyTree.com recognizes that deleting one account should not erase generations of family history. If a user who contributed parents, grandparents, great-grandparents, and children to a shared tree deletes their account, their account information may be removed, historical family records may remain if contributed by multiple users or designated as family heritage information, and other family administrators may continue managing the family tree.",
      },
      {
        n: 7, title: "Memorial Pages and Deceased Persons",
        body: "Memorial pages require special consideration. A memorial may include obituary information, photographs, life stories, family relationships, and tributes. A request to delete a memorial may require:",
        bullets: [
          "Proof of relationship",
          "Authorization from family representatives",
          "Consideration of other contributors",
        ],
        after_bullets: "IndianFamilyTree.com may preserve historical memorial records where appropriate while respecting legitimate family requests.",
      },
      {
        n: 8, title: "Legacy Accounts",
        body: "A Legacy Account is an account that continues to preserve family history after the original creator is no longer active. Legacy accounts help protect family trees, historical records, photographs, stories, and memorials. Users may designate a trusted person as a Legacy Manager. Examples include:",
        bullets: [
          "Spouse",
          "Child",
          "Sibling",
          "Relative",
          "Trusted family representative",
        ],
      },
      {
        n: 9, title: "Legacy Manager Responsibilities",
        body: "A Legacy Manager may maintain the family tree, add historical information, approve contributors, update memorial records, manage privacy settings, and preserve family documents. A Legacy Manager may not:",
        bullets: [
          "Misrepresent family history",
          "Remove legitimate family members",
          "Abuse access privileges",
          "Publish private information improperly",
        ],
      },
      {
        n: 10, title: "Selecting a Legacy Contact",
        body: "Users may optionally provide the name, relationship, email address, and authorization preferences of a Legacy Contact. The Legacy Contact may be activated after confirmation of the user's incapacity or death, or other verification procedures established by IndianFamilyTree.com.",
      },
      {
        n: 11, title: "Inactive Accounts",
        body: "An account may become inactive due to long-term non-use, failure to access the account, or a user request. IndianFamilyTree.com may send reminders, offer account recovery options, preserve family records, and apply retention policies. We will not automatically delete valuable family history without appropriate notice.",
      },
      {
        n: 12, title: "Death of an Account Holder",
        body: "If an account holder passes away, family members may request Account Transfer (transfer management to an approved Legacy Manager), Memorial Conversion (convert the profile into a memorial or historical family record), or Account Closure (request removal of personal information). Requests may require:",
        bullets: [
          "Proof of relationship",
          "Death certificate or public notice",
          "Authorization documentation",
        ],
      },
      {
        n: 13, title: "Data Retention",
        body: "IndianFamilyTree.com retains information only as long as necessary for providing services, maintaining family history, legal compliance, resolving disputes, and protecting platform integrity. Certain historical information may be retained in anonymized or archival form.",
      },
      {
        n: 14, title: "Backup and Technical Copies",
        body: "Due to technical systems, backups may temporarily contain deleted information. Complete removal from all backup systems may take reasonable time. Deleted information will not be used except where required for security, legal, or operational purposes.",
      },
      {
        n: 15, title: "Exporting Family Data",
        body: "Before deleting an account, users may request export of available information including family tree data, photos, documents, and stories. Export availability may depend on technical limitations, privacy rights of other individuals, and legal requirements.",
      },
      {
        n: 16, title: "Dispute Resolution",
        body: "Family members may disagree about deleting a family tree, removing ancestral information, managing memorials, or controlling historical records. IndianFamilyTree.com may:",
        bullets: [
          "Temporarily restrict changes",
          "Preserve previous versions",
          "Request documentation",
          "Facilitate fair review",
        ],
      },
      {
        n: 17, title: "Protection Against Unauthorized Deletion",
        body: "To protect family heritage, IndianFamilyTree.com may require additional verification before deleting large family trees, historical records, popular memorial pages, or shared family archives.",
      },
      {
        n: 18, title: "Privacy and Security",
        body: "Deletion requests and account changes are protected through identity verification, secure communication, access controls, and audit records.",
      },
      {
        n: 19, title: "Contact for Deletion Requests",
        body: "For account deletion, legacy requests, or privacy concerns, please reach out to us at:",
        bullets: [
          "IndianFamilyTree.com Data Protection Support",
          "Email: support@indianfamilytree.com",
          "Grievance Officer: Thomas Vellaringattu",
          "Address: 15166 SE Aston Loop, Portland, Oregon 97236, USA",
        ],
      },
      {
        n: 20, title: "Our Commitment",
        body: "A person's digital presence may end, but their story can continue. IndianFamilyTree.com is committed to respecting individual privacy while preserving the memories, relationships, and histories that connect generations. Accounts may expire. Memories should endure. Family legacies should continue.",
      },
    ],
  },

  // -------------------------------------------------------------------
  // IndianFamilyTree.com — Cookie Policy. Distinct from the generic
  // Social Pulse cookie policy so we can talk about DPDP-aligned
  // consent, third-party services (payments, analytics), and how
  // preferences can be managed on IFT specifically.
  // -------------------------------------------------------------------
  "ift-cookie-policy": {
    slug: "ift-cookie-policy",
    title: "Cookie Policy",
    heading: "IndianFamilyTree.com — Cookie Policy",
    effective: "August 1, 2026",
    updated: "August 15, 2026",
    intro:
      'Welcome to IndianFamilyTree.com ("IndianFamilyTree," "we," "us," or "our"). This Cookie Policy explains how we use cookies and similar technologies when you visit or use our website, applications, and related digital services. Cookies help us provide a better experience by allowing us to keep your account secure, remember your preferences, improve website performance, understand how users interact with our services, provide personalized features, and improve our genealogy and family history platform. This Cookie Policy should be read together with our Privacy Policy, Terms of Service, and User Consent & Authorization Policy.',
    sections: [
      {
        n: 2, title: "What Are Cookies?",
        body: "Cookies are small text files stored on your device when you visit a website. They help websites remember information such as:",
        bullets: [
          "Login status",
          "Language preferences",
          "Display settings",
          "User choices",
          "Website activity",
        ],
        after_bullets: "Cookies may be stored on computers, tablets, mobile phones, and other internet-connected devices.",
      },
      {
        n: 3, title: "Why IndianFamilyTree.com Uses Cookies",
        body: "We use cookies for several purposes: A. Essential Website Operations — user authentication, account security, session management, family tree access control, privacy settings, and fraud prevention. Without these cookies, important services may not work properly. B. Account and Personalization — user preferences, language choices, family tree settings, display preferences, and recent activity. C. Performance and Analytics — page visits, search activity, navigation patterns, device information (generally aggregated and non-identifying). D. Security — detecting suspicious activity, preventing unauthorized access, protecting accounts, and maintaining platform security. E. Payment and Subscription — supporting secure payment processing, subscription management, and transaction security (payment information is handled by authorized payment providers). F. AI and Feature Enhancement — as we develop AI-powered features, cookies may help support user preferences, feature settings, personalized recommendations, and improved user interactions.",
      },
      {
        n: 4, title: "Types of Cookies We May Use",
        body: "Session Cookies — temporary cookies deleted when you close your browser; used for login sessions, security, and navigation. Persistent Cookies — remain on your device for a specified period; used for remembering preferences, account settings, and returning users. First-Party Cookies — created directly by IndianFamilyTree.com; used for website functionality, user accounts, and family tree services. Third-Party Cookies — created by approved external service providers such as analytics, payment, security, and cloud technology providers.",
      },
      {
        n: 5, title: "Third-Party Services",
        body: "IndianFamilyTree.com may use trusted third-party services that may place cookies or similar technologies. These services may include:",
        bullets: [
          "Website analytics providers",
          "Payment processors",
          "Cloud infrastructure providers",
          "Authentication providers",
          "Security providers",
        ],
        after_bullets: "Third parties operate according to their own privacy policies.",
      },
      {
        n: 6, title: "Cookie Consent",
        body: "Where required by applicable laws, including Indian privacy requirements, IndianFamilyTree.com may request consent before placing non-essential cookies. Users may:",
        bullets: [
          "Accept cookies",
          "Reject non-essential cookies",
          "Modify cookie preferences",
        ],
        after_bullets: "Essential cookies required for website operation may continue to function.",
      },
      {
        n: 7, title: "Managing Cookie Preferences",
        body: "Users can control cookies through Website Cookie Settings (where available, users may adjust preferences through our cookie management tool) or through Browser Settings. Most browsers allow users to view cookies, delete cookies, block cookies, and receive alerts before cookies are stored. Please note that disabling cookies may affect certain features, including login functionality, family tree access, and personalization features.",
      },
      {
        n: 8, title: "Mobile Applications",
        body: "If IndianFamilyTree.com provides mobile applications, similar technologies may be used, including device identifiers, local storage, and application analytics. Users may manage permissions through their device settings.",
      },
      {
        n: 9, title: "Children's Privacy",
        body: "IndianFamilyTree.com may allow family members to record historical information about children as part of family genealogy. We do not knowingly use cookies to collect children's information for commercial purposes without appropriate safeguards. Parents or guardians should manage children's privacy settings.",
      },
      {
        n: 10, title: "Data Protection and Security",
        body: "We take reasonable measures to protect cookie-related information through secure systems, access controls, encryption where appropriate, and monitoring procedures. However, no internet transmission can be guaranteed to be completely secure.",
      },
      {
        n: 11, title: "International Users",
        body: "IndianFamilyTree.com serves families worldwide, including India, the United States, Europe, the Middle East, and other countries. Users outside India may have additional privacy rights depending on their location.",
      },
      {
        n: 12, title: "Changes to This Cookie Policy",
        body: "We may update this Cookie Policy periodically to reflect changes in technology, legal requirements, new website features, and improved privacy practices. Updated versions will be posted on this page.",
      },
      {
        n: 13, title: "Contact Us",
        body: "For questions regarding cookies or privacy, please reach out to us at:",
        bullets: [
          "IndianFamilyTree.com",
          "Email: support@indianfamilytree.com",
          "Data Protection / Grievance Officer: Thomas Vellaringattu",
          "Address: 15166 SE Aston Loop, Portland, Oregon 97236, USA",
        ],
      },
      {
        n: 14, title: "Our Commitment",
        body: "Cookies help us create a secure and personalized experience for families preserving their heritage. We use technology responsibly to support our mission: Connecting generations. Preserving memories. Protecting family history.",
      },
    ],
  },

  "foodpulse-cookie-policy": {
    slug: "foodpulse-cookie-policy",
    title: "FoodPulse Cookie Policy",
    heading: "FoodPulse — Cookie Policy",
    effective: "August 1, 2026",
    updated: "August 15, 2026",
    intro:
      'FoodPulse(™) is a property of Social Pulse® Inc platform. FoodPulse ("FoodPulse.net," "we," "our," or "us") uses cookies and similar technologies to provide, protect, improve, and personalize our services. This Cookie Policy explains what cookies are, why we use them, the types of cookies we use, how you can control them, and your rights regarding cookies.',
    sections: [
      {
        n: 1, title: "Introduction",
        body: "This Cookie Policy applies to:",
        bullets: [
          "FoodPulse.net",
          "FoodPulse mobile applications",
          "Marketplace services",
          "Business Portal",
          "AI services",
          "APIs (where applicable)",
          "All related FoodPulse services",
        ],
        after_bullets: "This Policy should be read together with our Privacy Policy, Terms of Service, and Community Guidelines.",
      },
      {
        n: 2, title: "What Are Cookies?",
        body: "Cookies are small text files placed on your computer, smartphone, tablet, or other device when you visit a website. Cookies allow websites to:",
        bullets: [
          "Remember your preferences",
          "Keep you signed in",
          "Improve security",
          "Measure website performance",
          "Personalize content",
          "Support marketplace functionality",
          "Improve user experience",
        ],
        footer_note: "Cookies do not normally contain information that directly identifies you, but they may be associated with information stored in your account.",
      },
      {
        n: 3, title: "Similar Technologies",
        body: "In addition to cookies, FoodPulse may use technologies including:",
        bullets: [
          "Web Beacons", "Pixel Tags", "Local Storage", "Session Storage",
          "SDKs (mobile applications)", "Device Identifiers", "Log Files",
          "Server-side Tracking", "API Tokens", "Authentication Tokens",
        ],
        footer_note: 'Throughout this Policy, these technologies are collectively referred to as "Cookies."',
      },
      {
        n: 4, title: "Why FoodPulse Uses Cookies",
        body: "Cookies help FoodPulse:",
        bullets: [
          "Operate the Platform", "Secure user accounts", "Authenticate users",
          "Remember login sessions", "Save language preferences",
          "Save accessibility settings", "Remember shopping carts",
          "Process marketplace transactions", "Display nearby food offers",
          "Improve AI recommendations", "Improve search",
          "Analyze website performance", "Detect fraud", "Prevent abuse",
          "Improve security", "Measure advertising effectiveness",
          "Personalize user experiences",
        ],
      },
      {
        n: 5, title: "Types of Cookies We Use",
        subgroups: [
          {
            subtitle: "A. Strictly Necessary Cookies",
            body: "These cookies are essential for the operation of FoodPulse. Examples include:",
            bullets: [
              "User authentication", "Security tokens", "Login sessions",
              "Shopping cart", "Marketplace checkout", "Fraud prevention",
              "Load balancing", "Network management",
            ],
            footer_note: "Without these cookies, FoodPulse cannot function properly. Consent is generally not required for these cookies where permitted by law.",
          },
          {
            subtitle: "B. Functional Cookies",
            body: "These cookies remember user preferences. Examples:",
            bullets: [
              "Language", "Country", "Theme (light/dark mode)",
              "Accessibility settings", "Saved searches",
              "Preferred marketplace location", "Favorite food categories",
            ],
          },
          {
            subtitle: "C. Performance Cookies",
            body: "These cookies help us understand how FoodPulse performs. Examples:",
            bullets: [
              "Pages visited", "Popular recipes", "Search performance",
              "Feature usage", "Loading speed", "Navigation behavior",
              "Error reporting",
            ],
            footer_note: "Performance cookies help us improve the Platform.",
          },
          {
            subtitle: "D. Analytics Cookies",
            body: "Analytics cookies help us understand how visitors use FoodPulse. Information may include:",
            bullets: [
              "Number of visitors", "Returning visitors", "Traffic sources",
              "Time spent on pages", "Device types", "Browser types",
              "Geographic regions (approximate)",
            ],
            footer_note: "Analytics data is generally aggregated and used to improve our services.",
          },
          {
            subtitle: "E. Personalization Cookies",
            body: "These cookies help personalize your experience. Examples:",
            bullets: [
              "Recommended recipes", "Nearby food deals", "Suggested businesses",
              "Personalized homepage", "AI recommendations", "Favorite products",
              "Previously viewed foods",
            ],
          },
          {
            subtitle: "F. Advertising Cookies",
            body: "Where permitted by law and with your consent where required, advertising cookies may be used to:",
            bullets: [
              "Display relevant advertisements",
              "Limit repeated advertisements",
              "Measure campaign effectiveness",
              "Support sponsored listings",
              "Measure marketplace promotions",
            ],
            footer_note: 'FoodPulse does not sell personal information for monetary consideration. Where applicable law defines certain advertising activities as a "sale" or "sharing" of personal information, users may exercise available opt-out rights.',
          },
          {
            subtitle: "G. Social Media Cookies",
            body: "If you interact with social media features on FoodPulse, cookies may be placed by third-party social media providers. These cookies are governed by the privacy policies of those providers.",
          },
        ],
      },
      {
        n: 6, title: "Session Cookies",
        body: "Session cookies:",
        bullets: [
          "Exist only while your browser remains open.",
          "Are automatically deleted when your session ends.",
          "Help maintain secure logins.",
          "Keep marketplace carts active.",
        ],
      },
      {
        n: 7, title: "Persistent Cookies",
        body: "Persistent cookies remain on your device after you leave FoodPulse until they expire or are deleted. These cookies help remember:",
        bullets: [
          "Login preferences", "Language", "User settings",
          "Accessibility preferences", "Saved marketplace preferences",
        ],
      },
      {
        n: 8, title: "Third-Party Cookies",
        body: "Some FoodPulse services may use trusted third-party providers for:",
        bullets: [
          "Website analytics", "Payment processing", "Fraud detection",
          "Maps", "Delivery services", "Customer support", "Advertising",
          "Embedded videos", "Business verification",
        ],
        footer_note: "Third parties may place cookies subject to their own privacy policies. FoodPulse does not control third-party cookies.",
      },
      {
        n: 9, title: "AI Services",
        body: "FoodPulse AI may use cookies or similar technologies to:",
        bullets: [
          "Remember conversation preferences", "Improve AI performance",
          "Prevent abuse", "Maintain session continuity",
          "Improve food recommendations",
        ],
        footer_note: "AI-related cookies are used only as necessary to provide and improve AI-powered services.",
      },
      {
        n: 10, title: "Marketplace Cookies",
        body: "Marketplace functionality may use cookies to:",
        bullets: [
          "Maintain shopping carts", "Remember favorite products",
          "Track order progress", "Save delivery preferences",
          "Support checkout", "Prevent fraudulent transactions",
        ],
        footer_note: "These cookies are generally necessary for marketplace operations.",
      },
      {
        n: 11, title: "Business Portal Cookies",
        body: "Business accounts may use cookies to:",
        bullets: [
          "Maintain secure sessions", "Access dashboards", "Manage inventory",
          "View analytics", "Process orders", "Upload products",
          "Verify businesses",
        ],
      },
      {
        n: 12, title: "Managing Cookies",
        body: "You can manage cookies through:",
        bullets: [
          "Browser settings", "Mobile device settings",
          "Cookie Preferences Center", "Privacy settings within FoodPulse",
          "Third-party opt-out tools (where available)",
        ],
        footer_note: "Blocking some cookies may reduce or disable certain FoodPulse features.",
      },
      {
        n: 13, title: "Browser Controls",
        body: "Most browsers allow you to:",
        bullets: [
          "View cookies", "Delete cookies", "Block cookies",
          "Block third-party cookies",
          "Delete cookies automatically when closing the browser",
          "Receive cookie notifications",
        ],
        footer_note: "Instructions vary by browser.",
      },
      {
        n: 14, title: "Do Not Track",
        body: 'Some browsers offer a "Do Not Track" (DNT) setting. Because there is no universally accepted standard for responding to DNT signals, FoodPulse currently responds to browser-based privacy signals only where required by applicable law. Where legally required, FoodPulse will recognize supported browser or device privacy preference signals.',
      },
      {
        n: 15, title: "Your Privacy Rights",
        body: "Depending on your location, you may have rights to:",
        bullets: [
          "Accept cookies", "Reject non-essential cookies", "Withdraw consent",
          "Access personal information", "Delete personal information",
          "Correct information", "Restrict processing",
          "Object to certain processing",
          "Opt out of targeted advertising where applicable",
        ],
        footer_note: "FoodPulse will honor these rights as required by applicable law.",
      },
      {
        n: 16, title: "International Users",
        body: "FoodPulse operates globally. Cookies may process information across multiple countries. Where required by law, international transfers of personal information collected through cookies will be protected using appropriate safeguards.",
      },
      {
        n: 17, title: "Cookie Retention",
        body: "Different cookies remain active for different periods. Typical durations:",
        bullets: [
          "Session Cookies — Until browser closes",
          "Authentication Cookies — Up to 30 days",
          "Preference Cookies — Up to 12 months",
          "Analytics Cookies — Up to 24 months",
          "Advertising Cookies — Up to 13 months (or as required by applicable law)",
          "Security Cookies — As necessary for security purposes",
        ],
        footer_note: "Actual retention periods may vary depending on legal requirements, technical needs, and user preferences.",
      },
      {
        n: 18, title: "Updates to This Cookie Policy",
        body: "FoodPulse may update this Cookie Policy to reflect:",
        bullets: [
          "Changes in technology", "New Platform features",
          "Legal requirements", "Security improvements",
          "Business practices",
        ],
        footer_note: 'Material changes will be communicated through appropriate channels, such as website notices, account notifications, or email where required by law. The "Last Updated" date at the top of this Policy indicates when the latest version became effective.',
      },
      {
        n: 19, title: "Contact Us",
        body: "If you have questions about this Cookie Policy or your cookie preferences, please contact FoodPulse through the contact methods listed on our website. Depending on your request, dedicated contact channels may be available for:",
        bullets: [
          "Privacy Inquiries", "Cookie Preferences",
          "Data Protection Requests", "Customer Support",
          "Business Accounts", "Security Reports",
        ],
      },
    ],
  },

  "foodpulse-privacy-policy": {
    slug: "foodpulse-privacy-policy",
    title: "FoodPulse Privacy Policy",
    heading: "FoodPulse — Privacy Policy",
    effective: "August 1, 2026",
    updated: "August 15, 2026",
    intro:
      'FoodPulse(™) is a property of Social Pulse® Inc platform. FoodPulse ("FoodPulse.net," "we," "our," or "us") is committed to protecting your privacy and handling your personal information responsibly. FoodPulse is a global platform designed to connect every participant in the food ecosystem — from farmers and fisheries to grocery stores, restaurants, food banks, businesses, researchers, and consumers. This Privacy Policy explains how FoodPulse collects, uses, shares, stores, and protects your information when you use our website, mobile applications, marketplace, Business Portal, AI services, Food Encyclopedia, recipes, community forums, educational resources, APIs, and related products and services. This Policy should be read together with our Terms of Service, Cookie Policy, Community Guidelines, Marketplace Policies, and Copyright Policy.',
    sections: [
      {
        n: 1, title: "Scope of this Privacy Policy",
        body: "This Privacy Policy applies to all FoodPulse services unless a separate privacy notice specifically applies. It governs information collected from:",
        bullets: [
          "Visitors", "Registered users", "Businesses",
          "Marketplace participants", "Farmers", "Restaurants",
          "Grocery stores", "Food manufacturers", "Researchers",
          "Advertisers", "Developers", "API users",
        ],
      },
      {
        n: 2, title: "Information We Collect",
        body: "FoodPulse collects information necessary to provide, improve, secure, and personalize our services. Depending on how you use the Platform, we may collect:",
        subgroups: [
          {
            subtitle: "Personal Information",
            bullets: [
              "Name", "Username", "Display name", "Email address",
              "Telephone number", "Postal address", "Country", "Language",
              "Profile photograph", "Date of birth (where required)",
              "Account credentials",
            ],
          },
          {
            subtitle: "Business Information",
            bullets: [
              "Business name", "Business registration", "Licenses",
              "Tax information", "Certifications", "Contact information",
              "Store locations", "Operating hours",
            ],
          },
          {
            subtitle: "Marketplace Information",
            bullets: [
              "Orders", "Listings", "Purchases", "Sales", "Payments",
              "Deliveries", "Inventory", "Pricing", "Coupons",
            ],
          },
          {
            subtitle: "Community Information",
            bullets: [
              "Reviews", "Recipes", "Photos", "Videos", "Comments",
              "Articles", "Questions", "Answers", "Ratings",
            ],
          },
          {
            subtitle: "Technical Information",
            bullets: [
              "IP address", "Browser", "Operating system", "Device type",
              "Mobile identifiers", "Cookies", "Analytics", "Session data",
            ],
          },
        ],
      },
      {
        n: 3, title: "Information You Provide",
        body: "You may provide information when you:",
        bullets: [
          "Create an account", "Complete your profile", "Purchase products",
          "Sell products", "Submit recipes", "Upload photographs",
          "Upload videos", "Contact support", "Subscribe to newsletters",
          "Join promotions", "Participate in surveys", "Submit feedback",
          "Verify your business", "Apply for partnerships",
        ],
      },
      {
        n: 4, title: "Information Collected Automatically",
        body: "FoodPulse automatically collects technical information to improve performance and security. Examples include:",
        bullets: [
          "Device identifiers", "Browser type", "Screen resolution",
          "Referring pages", "Session duration", "Pages visited",
          "Search queries", "Clickstream data", "Crash reports",
          "Performance logs", "Error reports",
        ],
      },
      {
        n: 5, title: "Location Information",
        body: "With your permission or as necessary to provide services, FoodPulse may collect:",
        bullets: [
          "GPS coordinates", "Approximate location", "Country", "State",
          "City", "Time zone",
        ],
        after_bullets: "Location information may be used to:",
        bullets_2: [
          "Display nearby food deals", "Recommend local businesses",
          "Show nearby food banks", "Improve search",
          "Display weather information", "Support local marketplace services",
        ],
        footer_note: "Users may control location permissions through their device settings.",
      },
      {
        n: 6, title: "Marketplace Information",
        body: "Marketplace activities may generate records including:",
        bullets: [
          "Orders", "Reservations", "Donations", "Product listings",
          "Inventory updates", "Payments", "Delivery tracking",
          "Pickup confirmations", "Refund requests", "Marketplace messages",
        ],
      },
      {
        n: 7, title: "Business Information",
        body: "Verified businesses may submit:",
        bullets: [
          "Business licenses", "Food safety permits",
          "Organic certifications", "Tax registrations",
          "Sustainability certifications", "Identity verification documents",
        ],
        footer_note: "Verification documents are used solely for verification, compliance, fraud prevention, and legal obligations.",
      },
      {
        n: 8, title: "AI Interactions",
        body: "When you interact with FoodPulse AI, we may collect:",
        bullets: [
          "Prompts", "Questions", "Responses", "Uploaded images",
          "Food identification requests", "Recipe requests", "Search requests",
        ],
        after_bullets: "This information may be used to:",
        bullets_2: [
          "Improve AI performance", "Detect misuse", "Improve accuracy",
          "Personalize experiences", "Enhance safety",
        ],
        footer_note: "Where required by law, AI training will occur only on data for which we have an appropriate legal basis.",
      },
      {
        n: 9, title: "Photos, Videos & Media",
        body: "Users may upload:",
        bullets: [
          "Food photographs", "Recipes", "Cooking videos", "Farm images",
          "Product images", "Educational materials",
        ],
        footer_note: "Depending on your settings, uploaded content may be publicly visible. Please avoid uploading personal or sensitive information that you do not wish to make public.",
      },
      {
        n: 10, title: "Cookies & Similar Technologies",
        body: "FoodPulse uses:",
        bullets: [
          "Essential cookies", "Functional cookies", "Performance cookies",
          "Analytics cookies", "Security cookies",
          "Advertising cookies (where permitted)", "Preference cookies",
        ],
        after_bullets: "Cookies help us:",
        bullets_2: [
          "Remember login sessions", "Improve performance",
          "Personalize content", "Prevent fraud", "Measure traffic",
          "Support advertising",
        ],
        footer_note: "Users can manage cookie preferences through browser settings or our Cookie Preferences Center, where available.",
      },
      {
        n: 11, title: "How We Use Your Information",
        body: "We use information to:",
        bullets: [
          "Operate FoodPulse", "Process marketplace transactions",
          "Verify businesses", "Improve AI", "Recommend food",
          "Recommend recipes", "Personalize searches", "Improve security",
          "Detect fraud", "Provide customer support", "Process subscriptions",
          "Send notifications", "Conduct analytics", "Improve accessibility",
          "Develop new features",
          "Support research using aggregated or anonymized information",
        ],
      },
      {
        n: 12, title: "Legal Bases for Processing (GDPR)",
        body: "Where the GDPR or similar laws apply, we process personal data based on one or more of the following legal bases:",
        bullets: [
          "Your consent", "Performance of a contract",
          "Compliance with legal obligations",
          "Protection of vital interests",
          "Performance of a task carried out in the public interest (where applicable)",
          "Our legitimate interests, provided those interests are not overridden by your rights and freedoms",
        ],
        footer_note: "Where processing is based on consent, you may withdraw that consent at any time, subject to applicable law.",
      },
      {
        n: 13, title: "Sharing Your Information",
        body: "FoodPulse may share information with:",
        bullets: [
          "Payment processors", "Delivery providers",
          "Cloud hosting providers", "Identity verification providers",
          "Analytics providers", "Customer support providers",
          "Government agencies where legally required",
          "Professional advisers",
          "Business partners, where necessary to provide requested services",
        ],
        footer_note: 'We do not sell your personal information to third parties for monetary consideration. Where we participate in interest-based advertising or similar activities, we will provide any notices and choices required by applicable law.',
      },
      {
        n: 14, title: "Public Content",
        body: "Certain information you choose to submit may be visible to other users, including:",
        bullets: [
          "Display name", "Profile photo", "Reviews", "Recipes", "Comments",
          "Business profiles", "Marketplace listings", "Public forum posts",
        ],
        footer_note: "Please consider carefully what you choose to make public.",
      },
      {
        n: 15, title: "International Data Transfers",
        body: "FoodPulse operates globally. Your information may be transferred to and processed in countries other than your own. Where required by law, we implement appropriate safeguards for international transfers, such as:",
        bullets: [
          "Standard Contractual Clauses", "Adequacy decisions",
          "Other legally recognized transfer mechanisms",
        ],
      },
      {
        n: 16, title: "Data Retention",
        body: "We retain personal information only for as long as reasonably necessary to:",
        bullets: [
          "Provide services", "Meet legal obligations", "Resolve disputes",
          "Enforce agreements", "Detect fraud", "Maintain security",
          "Support legitimate business purposes",
        ],
        footer_note: "Retention periods vary depending on the type of information and applicable legal requirements.",
      },
      {
        n: 17, title: "Your Privacy Rights",
        body: "Depending on your location, you may have rights to:",
        bullets: [
          "Access your personal information",
          "Correct inaccurate information", "Delete eligible information",
          "Restrict certain processing", "Object to certain processing",
          "Receive a portable copy of your data",
          "Withdraw consent where applicable",
          "Appeal certain privacy decisions",
          "Lodge a complaint with a supervisory authority",
        ],
        footer_note: "We will respond to verified requests in accordance with applicable law.",
      },
      {
        n: 18, title: "California Privacy Rights",
        body: "If you are a California resident, you may have rights under the California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act (CPRA), including rights to know, access, correct, delete, and limit certain uses of sensitive personal information, subject to applicable exceptions. FoodPulse will honor verified requests as required by California law.",
      },
      {
        n: 19, title: "European Privacy Rights",
        body: "If you are located in the European Economic Area (EEA), the United Kingdom, or Switzerland, you may have additional rights under applicable data protection laws, including GDPR or UK GDPR. Where required, FoodPulse will identify the relevant data controller, provide lawful processing information, and facilitate the exercise of your rights.",
      },
      {
        n: 20, title: "Children's Privacy",
        body: "FoodPulse is not directed to children under the minimum age required by applicable law to create an account without parental or guardian consent. If we learn that we have collected personal information from a child in violation of applicable law, we will take reasonable steps to delete that information or otherwise address it as required.",
      },
      {
        n: 21, title: "Data Security",
        body: "We implement reasonable administrative, technical, and organizational safeguards designed to protect personal information. These measures may include:",
        bullets: [
          "Encryption in transit",
          "Encryption at rest (where appropriate)", "Access controls",
          "Multi-factor authentication for administrative systems",
          "Security monitoring", "Logging and auditing",
          "Employee training", "Vendor risk assessments",
        ],
        footer_note: "No security system is perfect, and we cannot guarantee absolute security.",
      },
      {
        n: 22, title: "Marketing Communications",
        body: "Where permitted by law, we may send newsletters, product updates, educational content, and promotional communications. You may unsubscribe from marketing messages at any time using the unsubscribe link or your account preferences. Transactional and service-related communications may still be sent where necessary.",
      },
      {
        n: 23, title: "FoodPulse AI",
        body: "FoodPulse may use artificial intelligence to:",
        bullets: [
          "Improve search", "Recommend products",
          "Generate recipe suggestions",
          "Summarize educational information", "Translate content",
          "Assist with food identification", "Personalize user experiences",
        ],
        footer_note: "AI outputs may be inaccurate or incomplete. Users should independently verify important information, especially regarding health, nutrition, allergens, and food safety.",
      },
      {
        n: 24, title: "Third-Party Services",
        body: "FoodPulse may integrate with third-party providers for services such as payments, maps, analytics, messaging, cloud hosting, and delivery. Those providers operate under their own privacy policies, and FoodPulse is not responsible for their independent privacy practices.",
      },
      {
        n: 25, title: "Changes to this Privacy Policy",
        body: 'We may update this Privacy Policy periodically to reflect changes in our services, legal requirements, or business practices. When required by applicable law, we will provide notice of material changes through appropriate channels, such as email, website notices, or your account dashboard. The "Last Updated" date at the beginning of this Policy indicates when the most recent revision became effective.',
      },
      {
        n: 26, title: "Contact Us",
        body: "If you have questions about this Privacy Policy or wish to exercise your privacy rights, you may contact FoodPulse through the contact methods published on the Platform. Depending on the nature of your request, dedicated contact channels may be available for:",
        bullets: [
          "Privacy Requests", "Data Subject Rights", "Customer Support",
          "Business Accounts", "Legal Notices", "Security Reports",
          "Accessibility", "Media Inquiries",
        ],
      },
    ],
  },

  // ── NaturePulse.net child site ──────────────────────────────────
  // Referenced from the naturepulse footer via SITE_FOOTERS.naturepulse
  // in components/layout/Footer.jsx. Farmers Pulse content is part of
  // NaturePulse and covered by the same policy.
  "naturepulse-privacy-policy": {
    slug: "naturepulse-privacy-policy",
    title: "NaturePulse Privacy Policy",
    heading: "NaturePulse — Privacy Policy",
    effective: "August 23, 2026",
    updated: "August 23, 2026",
    intro:
      'NaturePulse.net, a division of Social Pulse Inc. ("NaturePulse," "we," "us," or "our") respects your privacy and is committed to protecting the personal information entrusted to us. NaturePulse is a platform for observing, documenting, learning about, and sharing information concerning nature, agriculture, biodiversity, weather, climate, wildlife, plants, habitats, and environmental change. NaturePulse includes specialized features and services such as Farmers Pulse, nature observations, photographs, geographic observations, community discussions, blogs, maps, alerts, research resources, artificial intelligence ("AI") tools, and other services that may be introduced over time. This Privacy Policy explains what information we may collect, how we use it, when it may be shared, the choices available to you, and how we work to protect it. By using NaturePulse.net or related NaturePulse services that reference this Privacy Policy, you acknowledge the practices described in this Policy.',
    sections: [
      {
        n: 1, title: "Our Privacy Principles",
        body: "NaturePulse is built around public participation and citizen observation, but participation should not require giving up unnecessary personal privacy. Our guiding principles are:",
        bullets: [
          "Collect information for legitimate purposes.",
          "Avoid collecting information we do not reasonably need.",
          "Clearly distinguish public contributions from private information.",
          "Give users reasonable control over their information.",
          "Protect precise location information where appropriate.",
          "Be especially careful with sensitive wildlife and farm locations.",
          "Use reasonable safeguards to protect information.",
          "Explain how AI may process submitted information.",
          "Be transparent about research and aggregated-data uses.",
          "Respect applicable privacy rights.",
        ],
      },
      {
        n: 2, title: "Information We May Collect",
        body: "The information we collect depends upon how you use NaturePulse. When you create an account, we may collect:",
        bullets: [
          "Name", "Username / display name", "Email address",
          "Telephone number", "Country", "State / province",
          "City or general location", "Profile photograph",
          "Preferred language", "Account preferences",
          "Authentication information", "Communication preferences",
        ],
        footer_note: "We may not require all of this information for every user.",
      },
      {
        n: 3, title: "Nature and Environmental Observations",
        body: "When you contribute an observation, we may collect:",
        bullets: [
          "Photographs", "Videos", "Audio", "Observation title",
          "Description", "Date", "Time", "Location", "GPS coordinates",
          "Species", "Plant name", "Animal name", "Bird name",
          "Insect name", "Environmental conditions", "Weather conditions",
          "Habitat information", "Tags", "Categories", "Comments",
          "Identification suggestions",
          "Other information you voluntarily provide",
        ],
        footer_note: "Some of this information may be publicly displayed when you choose to make an observation public.",
      },
      {
        n: 4, title: "Farmers Pulse Information",
        body: "Users participating in Farmers Pulse may provide additional agricultural information. This may include:",
        bullets: [
          "Farm name", "General farm location", "Field or plot information",
          "Crop types", "Crop varieties", "Planting dates", "Harvest dates",
          "Crop photographs", "Soil observations", "Irrigation information",
          "Rainfall observations", "Water conditions", "Pest observations",
          "Plant disease observations", "Weed observations",
          "Pollinator observations", "Livestock information",
          "Poultry information", "Agricultural practices",
          "Harvest observations", "Yield information", "Farm notes",
          "Farm diary entries", "Agricultural questions",
          "Farmer-to-farmer discussions", "Market observations",
          "Other farming information",
        ],
        footer_note: "Not all Farmers Pulse information needs to be public. Where functionality permits, users may be provided with choices regarding whether particular information is Private, Shared, Community-visible, or Public.",
      },
      {
        n: 5, title: "Location and GPS Information",
        body: "Location is an important component of NaturePulse because environmental and agricultural observations are often meaningful only when associated with a place. With appropriate permissions, NaturePulse may collect or process:",
        bullets: [
          "Country", "State / province", "District / county", "City",
          "Town", "Village", "General geographic area", "GPS coordinates",
          "Approximate device location",
          "Location associated with photographs",
          "Farm or field locations",
        ],
        subgroups: [
          {
            subtitle: "Precise Location",
            body: "We recognize that precise geographic coordinates can be sensitive. A precise location could potentially reveal:",
            bullets: [
              "A user's home", "A private farm",
              "A particular agricultural field", "A sensitive wildlife habitat",
              "An endangered species location", "A nesting site",
              "A valuable crop", "Private property",
              "Another sensitive location",
            ],
            footer_note: "NaturePulse may therefore provide tools to reduce, obscure, generalize, delay, or restrict precise geographic information. For example, a public observation might display the general village, city, county, or geographic region rather than the exact GPS coordinates. We strongly encourage users to consider the privacy and safety implications before publicly sharing exact locations.",
          },
        ],
      },
      {
        n: 6, title: "Location Information Embedded in Photographs",
        body: "Photographs taken with smartphones and digital cameras may contain metadata, including:",
        bullets: [
          "Date", "Time", "Camera information", "GPS coordinates",
          "Device information",
        ],
        footer_note: "NaturePulse may process this metadata to assist with organizing observations, suggesting locations, generating maps, or creating titles and descriptions. Where appropriate, NaturePulse may remove or obscure certain metadata from publicly displayed versions of photographs even when the underlying information is retained for the user's private records or platform functionality.",
      },
      {
        n: 7, title: "Public Information",
        body: "NaturePulse is partly designed as a public observation and knowledge-sharing platform. Information you intentionally designate as public may be visible to anyone. Public information may include:",
        bullets: [
          "Display name", "Profile photograph", "Public observations",
          "Photographs", "Videos", "Comments", "Articles",
          "Questions and answers", "Public farmer observations",
          "General observation location", "Date and time",
          "Species identification", "Crop information", "Tags",
          "Community contributions",
        ],
        footer_note: "Public information may also be indexed by search engines or shared through links and social media. Do not include information in a public contribution that you wish to keep private.",
      },
      {
        n: 8, title: "Private Information",
        body: "Information designated private will not ordinarily be publicly displayed. Private information may include, depending upon available features:",
        bullets: [
          "Personal farm records", "Private observations", "Farm diaries",
          "Private notes", "Precise farm locations",
          "Private field information", "Draft posts", "Personal calendars",
          "Account information", "Notification settings",
        ],
        footer_note: "Authorized personnel and service providers may access private information when reasonably necessary to operate, maintain, secure, troubleshoot, or support the Services.",
      },
      {
        n: 9, title: "How We Use Information",
        body: "NaturePulse may use collected information to:",
        bullets: [
          "Provide and operate NaturePulse", "Provide Farmers Pulse",
          "Maintain user accounts", "Store observations",
          "Organize photographs", "Create geographic maps",
          "Display environmental observations",
          "Display agricultural observations", "Identify plants and wildlife",
          "Assist with crop identification",
          "Assist with pest and disease identification",
          "Generate suggested descriptions", "Generate tags and categories",
          "Provide search functionality", "Personalize relevant content",
          "Provide weather-related information",
          "Generate agricultural calendars",
          "Provide notifications and alerts",
          "Facilitate community discussions",
          "Connect related observations", "Detect spam and abuse",
          "Protect users and the platform", "Improve our Services",
          "Analyze environmental patterns", "Analyze agricultural patterns",
          "Generate aggregated statistics", "Support citizen science",
          "Support research", "Develop new NaturePulse features",
          "Comply with applicable legal requirements",
        ],
      },
      {
        n: 10, title: "Artificial Intelligence",
        body: "NaturePulse may use AI and machine-learning systems to help process information. For example, AI may assist with:",
        bullets: [
          "Identifying plants", "Identifying animals", "Identifying birds",
          "Identifying insects", "Identifying crops",
          "Suggesting possible pests or diseases", "Analyzing photographs",
          "Reading photograph metadata", "Creating titles",
          "Generating descriptions", "Suggesting tags",
          "Translating content", "Summarizing information",
          "Categorizing observations", "Answering questions",
          "Detecting potentially inappropriate content",
          "Finding relationships among observations",
        ],
        footer_note: "Information submitted to an AI-enabled feature may be processed by NaturePulse systems or by technology providers working on our behalf. Where third-party AI providers process information for NaturePulse, we seek to use providers under appropriate contractual, privacy, and security arrangements. Users should avoid submitting unnecessary sensitive personal information to AI features.",
      },
      {
        n: 11, title: "Citizen Science and Research",
        body: "One of NaturePulse's goals is to create a valuable long-term record of our changing natural world. NaturePulse observations may contribute to citizen-science and research initiatives concerning subjects such as:",
        bullets: [
          "Biodiversity", "Climate change", "Agriculture", "Crop patterns",
          "Planting and harvesting seasons", "Pest distribution",
          "Plant diseases", "Pollinators", "Wildlife distribution",
          "Flowering patterns", "Migration", "Rainfall", "Drought",
          "Flooding", "Soil conditions", "Water conditions",
          "Habitat changes", "Environmental trends",
        ],
        after_bullets: "NaturePulse may create aggregated, statistical, anonymized, de-identified, or otherwise appropriately processed datasets from user observations. Such information may be used internally or, where appropriate, shared with:",
        bullets_2: [
          "Researchers", "Universities", "Schools",
          "Agricultural institutions", "Government agencies",
          "Environmental organizations", "Conservation organizations",
          "Nonprofit organizations",
          "Other research or public-interest organizations",
        ],
        footer_note: "We will seek to avoid unnecessarily identifying individual users when aggregated or de-identified information is sufficient for the intended purpose.",
      },
      {
        n: 12, title: "Your Photographs and Content",
        body: "You retain ownership of original photographs and other original content you contribute, subject to the license provided in our Terms of Service. NaturePulse may process your content to operate the Services, including to:",
        bullets: [
          "Store it", "Display it", "Resize it", "Create thumbnails",
          "Categorize it", "Analyze it", "Generate metadata",
          "Place observations on maps", "Create geographic collections",
          "Associate related observations",
          "Make public content searchable",
          "Include appropriate observations in aggregated research or educational resources",
        ],
        footer_note: "Please review the NaturePulse Terms of Service for additional information regarding content ownership and licenses.",
      },
      {
        n: 13, title: "Information Sharing",
        body: "We may share information in limited circumstances.",
        subgroups: [
          {
            subtitle: "With Your Direction",
            body: "We may share information when you intentionally publish, post, send, or otherwise direct us to share it.",
          },
          {
            subtitle: "Service Providers",
            body: "We may use third-party companies to provide services such as:",
            bullets: [
              "Website hosting", "Cloud storage", "Database hosting",
              "Email", "Authentication", "Mapping", "Analytics",
              "AI processing", "Image processing", "Security",
              "Notifications", "Customer support",
            ],
            footer_note: "These providers may process information necessary to perform services for us.",
          },
          {
            subtitle: "Research and Public-Interest Organizations",
            body: "We may provide appropriately aggregated, anonymized, de-identified, public, or otherwise authorized information for legitimate research, education, conservation, agricultural, environmental, or public-interest purposes.",
          },
          {
            subtitle: "Legal Requirements",
            body: "We may disclose information when reasonably necessary to comply with applicable law, court orders, legal processes, or valid governmental requests.",
          },
          {
            subtitle: "Safety and Security",
            body: "We may disclose information when reasonably necessary to investigate fraud, abuse, security threats, illegal activities, or credible threats to people or the Services.",
          },
        ],
      },
      {
        n: 14, title: "Sale of Personal Information",
        body: "NaturePulse does not intend to build its mission around selling users' personal information. If our practices change in a way that constitutes the \"sale\" or \"sharing\" of personal information under applicable privacy law, we will provide legally required disclosures and choices. Where required by applicable law, eligible users may have the right to opt out of the sale or sharing of personal information.",
      },
      {
        n: 15, title: "Cookies and Similar Technologies",
        body: "NaturePulse may use cookies, local storage, pixels, SDKs, and similar technologies. These technologies may be used for:",
        bullets: [
          "Authentication", "Remembering preferences", "Security",
          "Session management", "Analytics", "Performance",
          "Feature functionality", "Preventing abuse",
          "Understanding how the Services are used",
        ],
        footer_note: "Where legally required, users may be provided choices concerning non-essential cookies. Your browser may also allow you to control or delete cookies.",
      },
      {
        n: 16, title: "Analytics",
        body: "NaturePulse may use analytics services to understand how visitors use the platform. Analytics information may include:",
        bullets: [
          "Pages visited", "Features used", "Approximate geographic region",
          "Browser type", "Device type", "Operating system",
          "Referring page", "Date and time", "Session information",
          "IP address or related technical identifiers",
        ],
        footer_note: "We use this information to improve performance, usability, security, and features.",
      },
      {
        n: 17, title: "Communications and Notifications",
        body: "If you provide contact information, NaturePulse may send communications relating to:",
        bullets: [
          "Account verification", "Security", "Observations", "Comments",
          "Community activity", "Weather information",
          "Agricultural alerts", "Farmers Pulse updates",
          "Research participation", "Product changes",
          "Privacy or Terms updates", "Other requested notifications",
        ],
        footer_note: "You may be able to manage non-essential communications through your account settings or unsubscribe mechanisms. Certain security, account, transactional, or legally required communications may continue even if you opt out of promotional messages.",
      },
      {
        n: 18, title: "Children's Privacy",
        body: "NaturePulse may contain educational material useful to students and families. However, children require additional privacy protections. NaturePulse is not intended to knowingly collect personal information from children in violation of applicable law. Where parental or guardian consent is legally required, appropriate consent should be obtained before collecting personal information from a child. We may introduce special educational or student features with additional privacy protections. Parents or guardians who believe a child has submitted personal information improperly should contact us so that we can review the situation and take appropriate action.",
      },
      {
        n: 19, title: "Sensitive Wildlife Locations",
        body: "NaturePulse recognizes that publishing exact locations of endangered, threatened, rare, nesting, breeding, or commercially valuable species can cause harm. We may obscure, generalize, delay, or remove geographic information associated with sensitive species. NaturePulse may also restrict access to such information even when the contributor originally submitted precise coordinates. Protecting nature may sometimes require protecting its location.",
      },
      {
        n: 20, title: "Farm Privacy and Agricultural Security",
        body: "Farmers Pulse may contain information that could be sensitive to farmers. Examples include:",
        bullets: [
          "Exact farm coordinates", "Field boundaries", "Crop locations",
          "Livestock locations", "Production information",
          "Yield information", "Water sources", "Pest outbreaks",
          "Disease information", "Private farm notes",
        ],
        footer_note: "Farmers should carefully select what information they make public. NaturePulse may provide privacy controls allowing farmers to publish general regional information without revealing exact farm locations. We encourage farmers to keep commercially sensitive or security-sensitive information private unless they intentionally wish to share it.",
      },
      {
        n: 21, title: "Data Retention",
        body: "We retain personal information for as long as reasonably necessary for the purposes described in this Privacy Policy, including:",
        bullets: [
          "Providing the Services", "Maintaining accounts",
          "Preserving requested observations",
          "Maintaining historical environmental records",
          "Complying with legal obligations", "Resolving disputes",
          "Preventing abuse", "Protecting the security of NaturePulse",
        ],
        after_bullets: "Different categories of information may have different retention periods. Public environmental observations may have long-term historical or scientific value. If an account is deleted, certain information may remain where legally permitted in:",
        bullets_2: [
          "Backups", "Security logs", "Aggregated datasets",
          "Anonymized datasets", "Historical records",
          "Research datasets where removal is impractical or would compromise previously completed research",
        ],
        footer_note: "Where appropriate, personally identifying information may be removed while preserving the underlying environmental observation.",
      },
      {
        n: 22, title: "Data Security",
        body: "NaturePulse uses reasonable administrative, technical, and organizational safeguards designed to protect personal information. These may include, where appropriate:",
        bullets: [
          "Encryption in transit", "Secure authentication",
          "Access controls", "Restricted administrative access",
          "Database security", "Security monitoring", "Backups",
          "Software updates", "Service-provider security requirements",
          "Other reasonable safeguards",
        ],
        footer_note: "No internet service can guarantee absolute security. Users should also protect their accounts, devices, email accounts, and authentication methods.",
      },
      {
        n: 23, title: "Your Privacy Choices",
        body: "Depending upon your location and applicable law, you may have rights concerning your personal information. These may include the right to:",
        bullets: [
          "Know what information we collect", "Access personal information",
          "Request correction", "Request deletion",
          "Obtain information about how data is used",
          "Obtain information about certain disclosures",
          "Opt out of certain processing",
          "Opt out of certain sale or sharing where applicable",
          "Limit certain uses of sensitive personal information where applicable",
          "Withdraw consent where processing depends upon consent",
          "Appeal certain privacy decisions where applicable",
        ],
        footer_note: "Not every right applies in every jurisdiction or circumstance. We may need to verify your identity before fulfilling certain requests.",
      },
      {
        n: 24, title: "California Privacy Rights",
        body: "California residents may have additional rights under applicable California privacy laws. Depending upon whether NaturePulse / Social Pulse Inc. is subject to a particular requirement and whether an exception applies, these rights may include:",
        bullets: [
          "The right to know", "The right to access", "The right to delete",
          "The right to correct",
          "The right to opt out of qualifying sale or sharing",
          "The right to limit certain uses and disclosures of sensitive personal information",
          "The right not to receive discriminatory treatment for exercising applicable privacy rights",
        ],
        footer_note: "California users may contact us using the privacy contact information below.",
      },
      {
        n: 25, title: "International Users",
        body: "NaturePulse is designed with a worldwide community in mind. Privacy laws differ among countries. Users in certain jurisdictions may have additional rights concerning access, correction, deletion, portability, objection, restriction of processing, or withdrawal of consent. Where applicable privacy law provides additional rights, NaturePulse will seek to honor those rights.",
      },
      {
        n: 26, title: "Third-Party Websites",
        body: "NaturePulse may contain links to:",
        bullets: [
          "Government websites", "Universities", "Research organizations",
          "News publishers", "Agricultural resources",
          "Social media platforms", "Videos", "Weather services",
          "Mapping services", "Businesses", "Other websites",
        ],
        footer_note: "NaturePulse does not control the privacy practices of independent third-party websites. When you leave NaturePulse, the third party's privacy policy applies.",
      },
      {
        n: 27, title: "Business Transfers",
        body: "If Social Pulse Inc. or NaturePulse is involved in a merger, acquisition, restructuring, financing, sale of assets, or similar business transaction, information may be transferred as part of that transaction where legally permitted. Any successor organization receiving personal information will be required to handle it in accordance with applicable law and relevant privacy commitments.",
      },
      {
        n: 28, title: "Changes to This Privacy Policy",
        body: "NaturePulse will continue to evolve. We may update this Privacy Policy when our features, technologies, legal obligations, or information practices change. When significant changes are made, we may provide notice through:",
        bullets: [
          "NaturePulse.net", "The user's account", "Email",
          "An in-app notification", "Another appropriate method",
        ],
        footer_note: "The Last Updated date at the beginning of this Policy identifies the latest version.",
      },
      {
        n: 29, title: "Privacy Requests",
        body: "Users may contact NaturePulse to request access, correction, deletion, or other applicable privacy rights. Privacy requests may be submitted to Privacy Email: legal@naturepulse.net. We may request information reasonably necessary to verify your identity and protect your account from unauthorized requests. Where required by law, we will respond within the applicable legal timeframe.",
      },
      {
        n: 30, title: "Contact Information",
        body: "Questions regarding this Privacy Policy or NaturePulse's privacy practices may be directed to NaturePulse.net, a division of Social Pulse Inc.",
        bullets: [
          "Website: NaturePulse.net",
          "Privacy Email: legal@naturepulse.net",
          "Support Email: support@naturepulse.net",
          "Mailing Address: Social Pulse Inc., 15166 SE Aston Loop, Portland, OR 97236, United States",
        ],
      },
    ],
    closing:
      "NaturePulse is being created to document the changing pulse of our living planet. That mission depends upon trust. A photograph of a flower, a farmer's observation of rainfall, the arrival of a migratory bird, a changing shoreline, an unusual insect, a crop disease, or the first harvest of a season may become valuable information when documented over time. But valuable environmental information should not require unnecessary exposure of personal information. Our goal is therefore to balance open knowledge with personal privacy, citizen science with responsible data stewardship, location-based observations with protection of sensitive places, artificial intelligence with human judgment, and research value with respect for the people who contribute the data. We want NaturePulse and Farmers Pulse to become trusted places where people around the world can safely Observe · Document · Learn · Share · Protect — helping create a living record of our changing planet.",
  },
};

// Order + label used by the footer and BrowseAllMenu.
export const POLICY_LINKS = [
  { slug: "terms-of-use", label: "Terms of Use" },
  { slug: "privacy-policy", label: "Privacy Policy" },
  { slug: "cookie-policy", label: "Cookie Policy" },
  { slug: "memorial-policy", label: "Memorial & Obituary Policy" },
  { slug: "childrens-privacy-policy", label: "Children's Privacy Policy" },
];

// IndianFamilyTree.com-only footer link set. The Footer component
// chooses this list when the resolved site slug contains
// "indianfamilytree", falling back to POLICY_LINKS everywhere else.
export const POLICY_LINKS_IFT = [
  { slug: "ift-terms-of-service", label: "Terms of Service" },
  { slug: "ift-privacy-policy",   label: "Privacy Policy" },
  { slug: "ift-cookie-policy",    label: "Cookie Policy" },
  { slug: "memorial-policy",      label: "Memorial & Obituary Policy" },
  { slug: "ift-memorial-policy",  label: "Memorial Content Policy" },
  { slug: "ift-family-tree-policy", label: "Family Tree Data & Accuracy" },
  { slug: "ift-consent-policy",   label: "Consent & Authorization" },
  { slug: "ift-deletion-legacy-policy", label: "Data Deletion & Legacy" },
  { slug: "childrens-privacy-policy", label: "Children's Privacy Policy" },
];
